<template>
  <div class="assistant-root" :class="{ 'light-mode': !isDarkMode }">
    <!-- Assistant Header -->
    <div class="assistant-bar">
      <div class="assistant-header-layout">
        <div class="assistant-identity">
          <v-btn icon dark small @click="$emit('back')" class="assistant-back-btn">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="d-flex align-center">
              <span class="assistant-title">{{ monthName }}</span>
            </div>
            <div class="assistant-subtitle">Zuweisungs-Assistent</div>
          </div>
        </div>

        <!-- Week Navigation -->
        <div class="assistant-header-controls">
          <div class="assistant-week-navigation">
            <v-btn small depressed dark class="nav-btn" :disabled="currentWeekIndex === 0" @click="prevWeek">
              <v-icon left small>mdi-chevron-left</v-icon> Letzte Woche
            </v-btn>

            <div class="week-pill px-3 py-1 text-caption font-weight-bold">
              Woche {{ currentWeekIndex + 1 }}/{{ weeksInMonth.length }}: {{ currentWeekLabel }}
            </div>

            <v-btn small depressed dark class="nav-btn" :disabled="currentWeekIndex === weeksInMonth.length - 1" @click="nextWeek">
              Nächste Woche <v-icon right small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <v-divider vertical dark class="assistant-header-divider"></v-divider>

          <div class="assistant-status-controls">
            <!-- Staffing Bar with Hover Menu -->
            <v-menu open-on-hover offset-y bottom :close-on-content-click="false" rounded="xl" content-class="premium-hover-menu">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-on="on"
                  v-bind="attrs"
                  class="staffing-bar px-3 py-1 text-caption font-weight-bold d-flex align-center cursor-pointer"
                  style="gap: 8px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 8px">
                  <span class="grey--text">Soll-Erfüllung:</span>
                  <v-progress-linear
                    color="green accent-3"
                    height="6"
                    rounded
                    :value="Math.min(100, weeklyHoursFulfillmentPercentage)"
                    style="width: 70px"
                    class="d-inline-block"></v-progress-linear>
                  <span class="green--text text--lighten-3">
                    {{ totalWeeklyAssignedHours.toFixed(1) }}h / {{ totalWeeklyTargetHours.toFixed(0) }}h ({{ weeklyHoursFulfillmentPercentage }}%)
                  </span>
                </div>
              </template>

              <!-- Wochenübersicht Heatmap inside Hover Card -->
              <v-card
                class="premium-hover-card rounded-xl pa-4"
                min-width="500px"
                dark
                style="background: #1e1e1e !important; border: 1px solid rgba(255, 255, 255, 0.08) !important">
                <div class="d-flex align-center mb-3" style="gap: 8px">
                  <v-icon color="green accent-3" small>mdi-chart-line</v-icon>
                  <div class="text-subtitle-2 font-weight-bold white--text">Wochenübersicht & Soll-Stunden</div>
                </div>

                <div class="day-coverage-grid">
                  <div
                    v-for="day in currentWeekDays"
                    :key="day.dateStr"
                    class="day-coverage-card pa-3 rounded-lg d-flex flex-column align-start text-left grey darken-3 border-subtle"
                    :style="getDayAssignedShiftsCount(day.dateStr) === '0 Schichten' ? 'opacity: 0.5;' : ''">
                    <div class="text-caption font-weight-bold grey--text">{{ day.dayName.substring(0, 2) }}. {{ day.dayNum }}</div>
                    <div class="text-h6 font-weight-bold mt-1 white--text">{{ getDayPlannedHoursText(day.dateStr) }}</div>
                    <div class="text-caption mt-0.5 grey--text text--lighten-2" style="font-size: 0.72rem !important; line-height: 1.1">
                      {{ getDayAssignedShiftsCount(day.dateStr) }}
                    </div>
                  </div>
                </div>
              </v-card>
            </v-menu>

            <v-divider vertical dark class="assistant-status-divider"></v-divider>

            <!-- Strategy Selector Toggle -->
            <div class="strategy-selector px-3 py-1 text-caption font-weight-bold d-flex align-center">
              <span class="grey--text">Muster:</span>
              <v-menu offset-y dark rounded="xl" style="z-index: 10000" min-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on" class="white--text cursor-pointer d-flex align-center">
                    <v-icon left x-small color="grey lighten-1" class="mr-1">
                      {{ planningStrategy === "blockwise" ? "mdi-layers" : "mdi-sync" }}
                    </v-icon>
                    {{ planningStrategy === "blockwise" ? "Blockweise" : "Ausgeglichen (Rotation)" }}
                    <v-icon right x-small class="ml-1" color="white">mdi-chevron-down</v-icon>
                  </span>
                </template>
                <v-list dense class="grey darken-4 pa-2">
                  <v-list-item @click="planningStrategy = 'rotation'" class="rounded-lg mb-1 hover-row py-1">
                    <v-list-item-avatar class="ma-0 mr-2" size="28">
                      <v-icon small color="blue lighten-1">mdi-sync</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="white--text font-weight-bold">Ausgeglichen (Rotation)</v-list-item-title>
                      <v-list-item-subtitle class="grey--text text-caption" style="white-space: normal; line-height: 1.2">
                        Verteilt Schichten fair im Team. Die <strong>Monotonie-Bremse</strong> verhindert mehr als 3 gleiche Schichten in Folge.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="planningStrategy = 'blockwise'" class="rounded-lg hover-row py-1">
                    <v-list-item-avatar class="ma-0 mr-2" size="28">
                      <v-icon small color="teal lighten-1">mdi-layers</v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="white--text font-weight-bold">Blockweise (Früh/Spät)</v-list-item-title>
                      <v-list-item-subtitle class="grey--text text-caption" style="white-space: normal; line-height: 1.2">
                        Mitarbeiter behalten ihren Schichttyp bei. Die <strong>Monotonie-Bremse ist inaktiv</strong>, um feste Blöcke zu fördern.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <v-divider vertical dark class="assistant-header-divider"></v-divider>

          <!-- Stacked Smaller Action Buttons -->
          <div class="assistant-actions">
            <!-- Grouped Automation Dropdown -->
            <v-menu offset-y style="z-index: 10000" dark rounded="xl">
              <template v-slot:activator="{ on, attrs }">
                <v-badge color="indigo accent-3" content="!" overlap :value="suggestedFixedShiftsCount > 0" class="w-100" style="display: block">
                  <v-btn
                    x-small
                    depressed
                    color="indigo"
                    class="fixed-fill-btn font-weight-bold w-100 py-0"
                    style="height: 22px !important; font-size: 0.7rem !important; text-transform: none !important"
                    v-bind="attrs"
                    v-on="on"
                    :loading="autoFilling">
                    <v-icon left x-small color="white" class="mr-1">mdi-robot-outline</v-icon> Automatisierung
                    <v-icon right x-small color="white" class="ml-1">mdi-chevron-down</v-icon>
                  </v-btn>
                </v-badge>
              </template>
              <v-list dense class="grey darken-4 pa-1">
                <v-list-item @click="preAssignFixedShifts" class="rounded-lg mb-1 hover-row">
                  <v-list-item-icon class="mr-2 my-1"><v-icon small color="indigo accent-2">mdi-pin-outline</v-icon></v-list-item-icon>
                  <v-list-item-title class="white--text font-weight-semibold">Fixe Schichten eintragen</v-list-item-title>
                </v-list-item>
                <v-list-item @click="autoFillWeek" class="rounded-lg mb-1 hover-row">
                  <v-list-item-icon class="mr-2 my-1"><v-icon small color="blue accent-2">mdi-auto-fix</v-icon></v-list-item-icon>
                  <v-list-item-title class="white--text font-weight-semibold">Woche auto-füllen</v-list-item-title>
                </v-list-item>
                <v-list-item @click="confirmResetWeekDialog = true" class="rounded-lg hover-row">
                  <v-list-item-icon class="mr-2 my-1"><v-icon small color="error">mdi-refresh</v-icon></v-list-item-icon>
                  <v-list-item-title class="error--text font-weight-semibold">Woche zurücksetzen</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              x-small
              depressed
              color="success"
              class="finish-assistant-btn font-weight-bold w-100 py-0"
              style="height: 22px !important; font-size: 0.7rem !important; text-transform: none !important"
              @click="finishPlanning">
              <v-icon left x-small color="white" class="mr-1">mdi-calendar-check</v-icon> Plan prüfen
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Workspace -->
    <div class="assistant-body d-flex flex-nowrap" style="overflow: hidden; position: relative">
      <!-- Weekly Grid -->
      <div class="weekly-grid-container flex-grow-1 py-4 px-6 custom-scrollbar" style="overflow-y: auto">
        <div v-if="loading" class="d-flex justify-center align-center fill-height" style="min-height: 300px">
          <div class="text-center">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            <p class="mt-4 grey--text text--lighten-1">Lade Schichten…</p>
          </div>
        </div>

        <div v-else class="weekly-days-list">
          <!-- Glowing suggestion banner for fixed shifts -->
          <v-expand-transition>
            <div v-if="suggestedFixedShiftsCount > 0" class="mb-4">
              <v-alert
                border="left"
                colored-border
                color="indigo accent-3"
                dark
                class="rounded-xl premium-fixed-alert py-3 px-4 d-flex align-center justify-space-between flex-wrap"
                style="background: rgba(63, 81, 181, 0.08); border: 1px solid rgba(63, 81, 181, 0.15)">
                <div class="d-flex align-center">
                  <v-icon color="indigo accent-2" class="mr-3">mdi-magic-staff</v-icon>
                  <div>
                    <div class="text-subtitle-2 font-weight-bold white--text">
                      {{ suggestedFixedShiftsCount }} fixe Schichten können vor-eingetragen werden
                    </div>
                    <div class="text-caption grey--text">
                      Es wurden unbesetzte Schichten gefunden, für die Mitarbeiter mit passenden fixen Schichten verfügbar sind.
                    </div>
                  </div>
                </div>
                <v-btn
                  small
                  depressed
                  color="indigo accent-3"
                  class="font-weight-bold text-none rounded-lg ml-md-4 mt-2 mt-sm-0"
                  @click="preAssignFixedShifts"
                  :loading="autoFilling">
                  Jetzt eintragen <v-icon right small>mdi-arrow-right</v-icon>
                </v-btn>
              </v-alert>
            </div>
          </v-expand-transition>

          <div
            v-for="day in currentWeekDays"
            :key="day.dateStr"
            class="weekly-day-row mb-4 pa-4 rounded-xl d-flex align-stretch"
            :class="{
              'weekend-row': day.isWeekend,
              'today-row': day.isToday,
              'glowing-conflict-row': draggedEmployee && hasConflictForDay(draggedEmployee, day.dateStr),
            }">
            <!-- Day Left Column -->
            <div class="day-info-col d-flex flex-column justify-center pr-4 border-right" style="width: 170px; min-width: 170px">
              <div class="d-flex align-center justify-space-between w-100">
                <div class="text-subtitle-2 font-weight-bold white--text" style="white-space: nowrap !important">{{ day.dayName }}</div>
                <v-btn
                  x-small
                  depressed
                  class="special-day-btn text-caption font-weight-bold px-1.5 py-0.5 rounded-lg text-none"
                  style="height: 18px !important; font-size: 0.62rem !important"
                  @click.stop="openSpecialDayDialog(day.dateStr)"
                  title="Spezialtag konfigurieren">
                  <v-icon x-small class="mr-0.5" color="green accent-3">mdi-calendar-plus</v-icon>
                  <span class="green--text text--accent-3">Spezial</span>
                </v-btn>
              </div>
              <div class="text-h5 font-weight-bold mt-1" :class="day.isWeekend ? 'orange--text text--lighten-2' : 'green--text text--lighten-3'">
                {{ day.dayNum }}
              </div>
              <div class="text-caption grey--text text--lighten-1 mt-1">{{ day.formattedDate }}</div>

              <!-- Day Opening/Closing Status -->
              <div v-if="getShiftsForDay(day.dateStr).length > 0" class="d-flex flex-column mt-2" style="gap: 4px">
                <!-- Opening Shift Status -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      :color="getOpeningShiftStatus(day.dateStr).color"
                      :class="getOpeningShiftStatus(day.dateStr).textClass"
                      class="px-2 font-weight-bold"
                      style="height: 18px !important; justify-content: start; border-radius: 6px !important">
                      <v-icon x-small left class="mr-1">{{ getOpeningShiftStatus(day.dateStr).icon }}</v-icon>
                      <span class="text-truncate" style="max-width: 120px">{{ getOpeningShiftStatus(day.dateStr).label }}</span>
                    </v-chip>
                  </template>
                  <span>{{ getOpeningShiftStatus(day.dateStr).tooltip }}</span>
                </v-tooltip>

                <!-- Closing Shift Status -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      :color="getClosingShiftStatus(day.dateStr).color"
                      :class="getClosingShiftStatus(day.dateStr).textClass"
                      class="px-2 font-weight-bold"
                      style="height: 18px !important; justify-content: start; border-radius: 6px !important">
                      <v-icon x-small left class="mr-1">{{ getClosingShiftStatus(day.dateStr).icon }}</v-icon>
                      <span class="text-truncate" style="max-width: 120px">{{ getClosingShiftStatus(day.dateStr).label }}</span>
                    </v-chip>
                  </template>
                  <span>{{ getClosingShiftStatus(day.dateStr).tooltip }}</span>
                </v-tooltip>
              </div>
            </div>

            <!-- Day Shifts Column -->
            <div class="day-shifts-col flex-grow-1 pl-4 d-flex align-center flex-wrap" style="gap: 12px">
              <div v-if="getShiftsForDay(day.dateStr).length === 0" class="text-caption grey--text text--darken-1 py-2">
                <v-icon x-small color="grey darken-2" class="mr-1">mdi-calendar-blank</v-icon> Keine Schichten geplant
              </div>

              <div
                v-for="shift in getShiftsForDay(day.dateStr)"
                :key="shift._id"
                class="shift-card pa-3 rounded-lg pulse-hover border elevation-1 d-flex align-center"
                :class="{
                  'shift-assigned': shift.assignedEmployee,
                  'shift-unassigned': !shift.assignedEmployee,
                  'shift-selected': selectedShift && selectedShift._id === shift._id,
                  'shift-no-candidates': !shift.assignedEmployee && isShiftGap(shift._id),
                }"
                :style="{ borderLeft: `5px solid ${getShiftColor(shift)}` }"
                :aria-busy="isShiftAssignmentPending(shift) ? 'true' : 'false'"
                @click="selectShift(shift)"
                @dragover.prevent
                @drop="onCandidateDrop($event, shift)">
                <!-- Delete Button (Red Cross) for unassigned shift -->
                <v-btn
                  v-if="!shift.assignedEmployee"
                  icon
                  x-small
                  color="red"
                  class="delete-shift-x-btn"
                  :disabled="isShiftAssignmentPending(shift)"
                  @click.stop="deleteShift(shift)"
                  title="Schicht löschen">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>

                <div class="flex-grow-1 pr-3" style="max-width: 220px">
                  <div class="text-subtitle-2 font-weight-bold truncate-text white--text">
                    <v-progress-circular v-if="isShiftAssignmentPending(shift)" indeterminate :size="12" :width="2" color="white" class="mr-1" />
                    {{ shift.shiftTemplate ? shift.shiftTemplate.name : "Custom" }}
                  </div>
                  <div class="text-caption grey--text text--lighten-2 mt-1">
                    <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon> {{ formatShiftTime(shift) }}
                  </div>
                </div>

                <div class="shift-assignee-status pl-2 border-left-dashed">
                  <div v-if="shift.assignedEmployee" class="d-flex align-center">
                    <v-menu
                      open-on-hover
                      :close-on-content-click="false"
                      offset-y
                      bottom
                      min-width="320px"
                      dark
                      rounded="xl"
                      content-class="premium-hover-menu">
                      <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                        <div class="d-flex align-center">
                          <v-avatar v-on="menuOn" v-bind="menuAttrs" size="32" class="mr-2 elevation-1 cursor-pointer">
                            <img :src="getEmployeePicture(shift.assignedEmployee)" />
                          </v-avatar>
                          <div style="max-width: 120px" class="d-flex align-center flex-wrap">
                            <span class="text-caption font-weight-bold white--text truncate-text mr-1">
                              {{ formatEmployeeShortName(resolveEmployee(shift.assignedEmployee).fullName) }}
                            </span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  x-small
                                  :color="isAutomatic(shift) ? 'indigo lighten-3' : 'orange lighten-3'"
                                  v-bind="attrs"
                                  v-on="on"
                                  class="ml-0.5">
                                  {{ isAutomatic(shift) ? "mdi-robot-outline" : "mdi-hand-pointing-right" }}
                                </v-icon>
                              </template>
                              <span>{{ isAutomatic(shift) ? "Automatisch zugewiesen" : "Manuell zugewiesen" }}</span>
                            </v-tooltip>
                            <v-tooltip bottom v-if="hasAssignedWish(shift)">
                              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-icon x-small color="pink" class="ml-1" v-bind="tooltipAttrs" v-on="tooltipOn">mdi-heart</v-icon>
                              </template>
                              <span>Wunschschicht erfüllt!</span>
                            </v-tooltip>
                            <v-tooltip bottom v-if="isOverrideShift(shift)">
                              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-icon x-small color="orange" class="ml-1" v-bind="tooltipAttrs" v-on="tooltipOn">mdi-alert-decagram</v-icon>
                              </template>
                              <span>Ausnahme: {{ shift.allocationNote }}</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </template>

                      <shift-management-employee-planning-hover
                        v-if="getEmployeePlanningOverview(shift.assignedEmployee)"
                        :overview="getEmployeePlanningOverview(shift.assignedEmployee)"
                        :picture="getEmployeePicture(shift.assignedEmployee)"
                        :is-dark-mode="isDarkMode" />
                    </v-menu>
                    <v-btn
                      icon
                      x-small
                      color="amber"
                      class="ml-2 hover-show"
                      @click.stop="openNoteDialog(shift.assignedEmployee, shift.date)"
                      title="Tagesnotiz">
                      <v-icon x-small>mdi-note-edit-outline</v-icon>
                    </v-btn>
                    <v-btn icon x-small color="grey lighten-1" class="ml-1 hover-show" :disabled="isShiftAssignmentPending(shift)" @click.stop="removeAssignment(shift)">
                      <v-icon x-small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <div v-else-if="isShiftGap(shift._id)" class="status-badge status-danger">
                    <v-icon x-small color="red lighten-1" class="mr-1">mdi-alert-octagon</v-icon> Gaps
                  </div>
                  <div v-else class="status-badge status-open"><span class="status-dot"></span> Offen</div>
                </div>
              </div>

              <!-- Add Shift Placeholder Card -->
              <div
                class="shift-card-add pa-3 rounded-lg d-flex align-center justify-center cursor-pointer elevation-1"
                @click.stop="openAddShiftDialog(day.dateStr)"
                style="
                  border: 2px dashed rgba(255, 255, 255, 0.15);
                  background: rgba(255, 255, 255, 0.02);
                  height: 54px;
                  min-width: 54px;
                  width: 54px;
                  border-radius: 12px;
                  transition: all 0.2s;
                "
                @mouseover="
                  $event.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)'
                  $event.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'
                "
                @mouseleave="
                  $event.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)'
                  $event.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)'
                "
                title="Schicht hinzufügen">
                <v-icon color="grey lighten-1">mdi-plus</v-icon>
              </div>

              <!-- Standortübergreifende Mitarbeiter-Karten (Anderes Center / Externe Schichten) -->
              <v-tooltip bottom v-for="(ext, idx) in getExternalEmployeesForDay(day.dateStr)" :key="'ext-' + ext.employee._id + '-' + idx">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-on="on"
                    v-bind="attrs"
                    class="pa-2 px-3 rounded-lg d-flex align-center"
                    :style="{
                      background: ext.isVirtual ? 'rgba(76, 175, 80, 0.04)' : 'rgba(255, 255, 255, 0.03)',
                      border: ext.isVirtual ? '1px solid rgba(76, 175, 80, 0.3) !important' : '1px dashed rgba(255, 255, 255, 0.1) !important',
                      opacity: ext.isVirtual ? '1.0' : '0.7',
                      cursor: 'default',
                      gap: '8px',
                      'min-height': '54px',
                      height: 'auto',
                      transition: 'all 0.2s',
                    }"
                    @mouseover="
                      $event.currentTarget.style.opacity = '1.0'
                      $event.currentTarget.style.background = ext.isVirtual ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 255, 255, 0.06)'
                    "
                    @mouseleave="
                      $event.currentTarget.style.opacity = ext.isVirtual ? '1.0' : '0.7'
                      $event.currentTarget.style.background = ext.isVirtual ? 'rgba(76, 175, 80, 0.04)' : 'rgba(255, 255, 255, 0.03)'
                    ">
                    <v-avatar size="24" class="elevation-1 grey darken-2">
                      <img :src="getEmployeePicture(ext.employee)" @error="useDefaultEmployeePicture" />
                    </v-avatar>

                    <v-icon v-if="ext.hasShift && ext.isVirtual" x-small color="teal" class="ml-n2 mr-1">mdi-check-circle</v-icon>

                    <div class="d-flex flex-column" style="max-width: 140px">
                      <span class="text-caption font-weight-bold white--text truncate-text">
                        {{ ext.employee.fullName }}
                      </span>
                      <span class="text-caption grey--text font-weight-medium truncate-text" style="font-size: 0.68rem !important; line-height: 1.1">
                        {{ ext.statusText }}
                      </span>
                      <!-- Works on this day (Arbeitet an diesem Tag) toggle -->
                      <v-btn
                        v-if="!ext.hasShift"
                        x-small
                        dense
                        outlined
                        color="teal lighten-2"
                        class="teal--text text--lighten-4 mt-1 font-weight-bold text-none rounded-lg"
                        style="height: 20px; font-size: 0.63rem; border: 1px solid rgba(76, 175, 80, 0.4)"
                        @click.stop="confirmExternalWork(ext.employee, day.dateStr)">
                        Arbeitet auswärts
                      </v-btn>

                      <!-- Cancel virtual confirmation (Aufheben) -->
                      <v-btn
                        v-if="ext.hasShift && ext.isVirtual"
                        x-small
                        dense
                        outlined
                        color="red lighten-1"
                        class="red--text text--lighten-3 mt-1 font-weight-bold text-none rounded-lg"
                        style="height: 20px; font-size: 0.63rem; border: 1px solid rgba(229, 115, 115, 0.4)"
                        @click.stop="cancelExternalWork(ext.shiftId)">
                        Aufheben
                      </v-btn>
                    </div>
                  </div>
                </template>
                <span>
                  {{ ext.employee.fullName }}<br />
                  <strong>{{ ext.statusText }}</strong>
                  <div v-if="!ext.hasShift" class="teal--text text--lighten-4 mt-1" style="font-size: 0.75rem; max-width: 250px; line-height: 1.2">
                    Klicke „Arbeitet auswärts“, um diesen Tag manuell als gearbeiteten Tag für Serien (Streaks) zu werten.
                  </div>
                  <div
                    v-if="ext.hasShift && ext.isVirtual"
                    class="red--text text--lighten-3 mt-1"
                    style="font-size: 0.75rem; max-width: 250px; line-height: 1.2">
                    Klicke „Aufheben“, um diese Bestätigung wieder zu löschen.
                  </div>
                </span>
              </v-tooltip>
            </div>
          </div>

          <!-- Heatmap widget moved as hover tooltip to the header bar -->
        </div>
      </div>

      <!-- Right Side Planning Drawer -->
      <div class="assistant-drawer border-left d-flex flex-column" :class="isDarkMode ? 'grey darken-4' : 'white'">
        <!-- Drawer Header -->
        <div
          class="drawer-header pa-4 border-bottom d-flex align-center justify-space-between flex-shrink-0"
          :class="[isDarkMode ? 'grey darken-3' : 'grey lighten-5', { 'team-radar-drawer-header': !selectedShift }]">
          <div v-if="selectedShift" class="w-100 pr-3">
            <div class="d-flex align-center justify-space-between w-100">
              <span
                class="premium-overtitle-badge px-2 py-0.5 rounded-lg text-caption font-weight-bold d-flex align-center"
                style="
                  background: rgba(68, 138, 255, 0.1);
                  border: 1px solid rgba(68, 138, 255, 0.25);
                  color: #448aff !important;
                  font-size: 0.65rem !important;
                  letter-spacing: 0.5px;
                  text-transform: uppercase;
                ">
                <span class="pulsing-dot-indigo mr-1.5"></span> Schicht konfigurieren
              </span>
              <v-btn icon dark small @click="selectedShift = null" class="mr-n2"><v-icon small>mdi-close</v-icon></v-btn>
            </div>
            <div class="text-h6 font-weight-bold white--text mt-2 mb-2 truncate-text" style="letter-spacing: -0.3px; line-height: 1.2">
              {{ selectedShift.shiftTemplate ? selectedShift.shiftTemplate.name : "Custom" }}
            </div>
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="drawer-header-pill px-2.5 py-0.5 text-caption font-weight-medium white--text d-flex align-center">
                <v-icon x-small class="mr-1" color="grey lighten-1">mdi-calendar-range</v-icon>
                {{ $moment(selectedShift.date).format("dddd, DD.MM") }}
              </span>
              <span class="drawer-header-pill px-2.5 py-0.5 text-caption font-weight-medium white--text d-flex align-center">
                <v-icon x-small class="mr-1" color="grey lighten-1">mdi-clock-outline</v-icon>
                {{ formatShiftTime(selectedShift) }}
              </span>
            </div>
          </div>
          <div v-else class="w-100 pr-3">
            <div class="d-flex align-center mb-1">
              <span
                class="premium-overtitle-badge team-radar-status-badge px-2 py-0.5 rounded-lg text-caption font-weight-bold d-flex align-center"
                style="
                  background: rgba(0, 230, 118, 0.1);
                  border: 1px solid rgba(0, 230, 118, 0.25);
                  color: #00e676 !important;
                  font-size: 0.76rem !important;
                  letter-spacing: 0.5px;
                  text-transform: uppercase;
                ">
                <span class="pulsing-dot-green mr-1.5"></span> Team-Radar aktiv
              </span>
            </div>
            <div class="team-radar-title text-h6 font-weight-bold white--text mt-2" style="letter-spacing: -0.3px">Planungszentrale</div>
            <div class="team-radar-subtitle text-caption grey--text mt-1">{{ currentWeekLabel }} · Schicht anklicken für ROODY</div>
          </div>
        </div>

        <!-- Drawer Body -->
        <div
          class="drawer-body flex-grow-1 pa-4 custom-scrollbar d-flex flex-column"
          :class="{ 'team-radar-drawer-body': !selectedShift }"
          style="overflow-y: auto">
          <div v-if="!selectedShift" class="team-radar d-flex flex-column flex-grow-1">
            <div class="team-radar-callout">
              <span class="team-radar-callout-icon"><v-icon small>mdi-robot-outline</v-icon></span>
              <div>
                <strong>Teamlage auf einen Blick</strong>
                <span>Für den Wochenblick eine Person hovern oder antippen. Für ROODY links eine offene Schicht wählen.</span>
              </div>
            </div>

            <div class="team-radar-summary">
              <div class="team-radar-summary-card is-team">
                <v-icon small>mdi-account-group-outline</v-icon>
                <strong>{{ teamOverviewStats.total }}</strong>
                <span>im Team</span>
              </div>
              <div class="team-radar-summary-card is-hours">
                <v-icon small>mdi-timer-sand</v-icon>
                <strong>{{ teamOverviewStats.openHours.toFixed(0) }}h</strong>
                <span>Studio-Soll offen</span>
              </div>
              <div class="team-radar-summary-card is-open-shifts" :class="{ 'is-complete': !unassignedShiftsInCurrentWeek.length }">
                <v-icon small>mdi-calendar-alert-outline</v-icon>
                <strong>{{ unassignedShiftsInCurrentWeek.length }}</strong>
                <span>offene Schichten</span>
              </div>
            </div>

            <div class="team-radar-toolbar">
              <v-btn-toggle v-model="teamOverviewFilter" mandatory dense class="team-radar-filter">
                <v-btn x-small value="all">Alle</v-btn>
                <v-btn x-small value="capacity">Kapazität</v-btn>
                <v-btn x-small value="conflicts">Konflikte</v-btn>
              </v-btn-toggle>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    class="team-radar-sort"
                    v-bind="attrs"
                    v-on="on"
                    @click="teamOverviewSort = teamOverviewSort === 'need' ? 'name' : 'need'">
                    <v-icon small>{{ teamOverviewSort === "need" ? "mdi-sort-clock-ascending-outline" : "mdi-sort-alphabetical-ascending" }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ teamOverviewSort === "need" ? "Nach Planungsbedarf" : "Alphabetisch" }}</span>
              </v-tooltip>
            </div>

            <div v-if="employeePlanningOverview.length === 0" class="team-radar-empty">
              <v-icon color="grey" class="mb-2">mdi-filter-off-outline</v-icon>
              <span>Für diesen Filter gibt es aktuell keine Mitarbeitenden.</span>
            </div>

            <div v-else class="team-radar-list">
              <v-menu
                v-for="row in employeePlanningOverview"
                :key="row.id"
                open-on-hover
                :open-on-click="true"
                :close-on-content-click="false"
                :close-delay="160"
                left
                offset-x
                content-class="premium-hover-menu">
                <template v-slot:activator="{ on, attrs }">
                  <div class="team-radar-person" :class="`is-${row.hoursState}`" v-bind="attrs" v-on="on">
                    <div class="team-radar-person-head">
                      <v-avatar size="46" class="team-radar-avatar">
                        <img :src="getEmployeePicture(row.employee)" @error="useDefaultEmployeePicture" />
                      </v-avatar>
                      <div class="team-radar-person-name">
                        <strong>{{ row.name }}</strong>
                        <span>{{ row.percentage }}% Planungspensum Center</span>
                      </div>
                      <span class="team-radar-balance" :class="`is-${row.hoursState}`">{{ row.hoursStateLabel }}</span>
                    </div>

                    <div class="team-radar-hours-summary">
                      <div>
                        <span>Monatsplanung</span>
                        <strong
                          >{{ row.plannedHours.toFixed(1) }}h <small>von {{ row.targetHours.toFixed(1) }}h Soll</small></strong
                        >
                      </div>
                      <b>{{ Math.max(0, Math.round(row.progress)) }}%</b>
                    </div>

                    <div class="team-radar-progress">
                      <span :class="`is-${row.hoursState}`" :style="{ width: Math.min(100, Math.max(0, row.progress)) + '%' }"></span>
                    </div>

                    <div class="team-radar-kpis">
                      <span class="is-week">
                        <v-icon x-small>mdi-calendar-week</v-icon>{{ row.weekHours.toFixed(1) }}h · {{ row.weekShiftCount }}
                        {{ row.weekShiftCount === 1 ? "Schicht" : "Schichten" }}
                      </span>
                      <span v-if="unassignedShiftsInCurrentWeek.length" class="is-match">
                        <v-icon x-small>mdi-account-check-outline</v-icon>{{ row.availableOpenCount }}/{{ unassignedShiftsInCurrentWeek.length }}
                        offen passend
                      </span>
                      <span v-else class="is-complete"><v-icon x-small>mdi-check-all</v-icon>Woche besetzt</span>
                    </div>

                    <div v-if="row.conflictReasons.length" class="team-radar-warning">
                      <v-icon x-small>mdi-alert-circle-outline</v-icon>
                      <span>{{ row.conflictReasons[0] }}</span>
                      <small v-if="row.conflictReasons.length > 1">+{{ row.conflictReasons.length - 1 }}</small>
                    </div>
                  </div>
                </template>

                <shift-management-employee-planning-hover :overview="row" :picture="getEmployeePicture(row.employee)" :is-dark-mode="isDarkMode" />
              </v-menu>
            </div>
          </div>
          <div v-else-if="loadingCandidates" class="d-flex justify-center align-center fill-height py-8 flex-grow-1">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <div v-else>
            <!-- If already assigned: Show beautiful staffed banner -->
            <v-expand-transition>
              <div
                v-if="selectedShift.assignedEmployee"
                class="mb-5 pa-4 rounded-xl"
                :style="
                  assignedEmployeeConflict
                    ? 'background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(255, 82, 82, 0.45) !important'
                    : 'background: rgba(76, 175, 80, 0.08); border: 1px solid rgba(76, 175, 80, 0.25) !important'
                ">
                <div class="d-flex align-center justify-space-between mb-3">
                  <div class="d-flex align-center" style="gap: 6px">
                    <v-icon :color="assignedEmployeeConflict ? 'red lighten-1' : 'green accent-3'" small>
                      {{ assignedEmployeeConflict ? "mdi-alert-circle" : "mdi-check-circle" }}
                    </v-icon>
                    <span class="text-subtitle-2 font-weight-bold white--text">
                      {{ assignedEmployeeConflict ? "Planungskonflikt bei besetzter Schicht" : "Schicht ist besetzt" }}
                    </span>
                  </div>
                  <v-btn
                    x-small
                    depressed
                    color="error"
                    class="font-weight-bold text-none rounded-lg"
                    style="height: 20px !important"
                    @click="removeAssignment(selectedShift)">
                    <v-icon left x-small>mdi-close</v-icon> Freigeben
                  </v-btn>
                </div>

                <div class="d-flex align-center">
                  <v-avatar
                    size="36"
                    class="mr-3 premium-avatar-glow"
                    style="border: 2px solid rgba(76, 175, 80, 0.3) !important; box-shadow: 0 0 10px rgba(76, 175, 80, 0.1) !important">
                    <img :src="getEmployeePicture(selectedShift.assignedEmployee)" />
                  </v-avatar>
                  <div>
                    <div class="text-subtitle-2 font-weight-bold white--text">
                      {{
                        selectedShift.assignedEmployee.fullName ||
                        (resolveEmployee(selectedShift.assignedEmployee) && resolveEmployee(selectedShift.assignedEmployee).fullName) ||
                        "Mitarbeiter"
                      }}
                    </div>
                    <div class="text-caption grey--text" style="font-size: 0.72rem !important">
                      {{ selectedShift.allocationNote || "Manuell zugewiesen" }}
                    </div>
                  </div>
                </div>
                <div v-if="assignedEmployeeConflict" class="mt-3 pt-3" style="border-top: 1px solid rgba(255, 82, 82, 0.22)">
                  <div class="text-caption red--text text--lighten-2 font-weight-bold mb-1">
                    Diese Zuweisung widerspricht den aktuellen Monatsregeln:
                  </div>
                  <div
                    v-for="(reason, reasonIndex) in assignedEmployeeConflict.reasons || []"
                    :key="'assigned-conflict-' + reasonIndex"
                    class="d-flex align-start text-caption red--text text--lighten-3 mb-1">
                    <v-icon x-small color="red lighten-2" class="mr-1 mt-0.5">mdi-close-octagon-outline</v-icon>
                    <span>{{ reason }}</span>
                  </div>
                  <div class="text-caption grey--text text--lighten-1 mt-2">
                    Mit „Freigeben“ bleibt die Schicht bestehen und kann danach kontrolliert neu zugewiesen werden.
                  </div>
                </div>
              </div>
            </v-expand-transition>

            <!-- Stammschicht Eingehalten Positive Banner -->
            <v-expand-transition>
              <div
                v-if="
                  selectedShift.assignedEmployee &&
                  !candidates.reservationBypassed &&
                  candidates.reservedEmployeeId &&
                  String(selectedShift.assignedEmployee._id || selectedShift.assignedEmployee) === String(candidates.reservedEmployeeId)
                "
                class="mb-5 pa-4 rounded-xl"
                style="background: rgba(76, 175, 80, 0.05); border: 1px solid rgba(76, 175, 80, 0.2) !important">
                <div class="d-flex align-start" style="gap: 10px">
                  <v-icon color="green accent-3" class="mt-0.5">mdi-star-decagram</v-icon>
                  <div>
                    <div
                      class="text-caption font-weight-bold green--text text--lighten-2"
                      style="font-size: 0.72rem !important; letter-spacing: 0.5px">
                      STAMMSCHICHT ZUGEWIESEN
                    </div>
                    <div class="text-caption grey--text text--lighten-2 mt-1" style="font-size: 0.72rem !important; line-height: 1.4">
                      Diese Schicht ist als fixe Stammschicht für
                      <strong>{{
                        candidates.reservedEmployeeName ||
                        (resolveEmployee(selectedShift.assignedEmployee) && resolveEmployee(selectedShift.assignedEmployee).fullName)
                      }}</strong>
                      hinterlegt und wurde erfolgreich zugewiesen.
                    </div>
                  </div>
                </div>
              </div>
            </v-expand-transition>

            <div :class="{ 'assigned-inactive-section': selectedShift.assignedEmployee }">
              <!-- Reservation Bypass Warning Banner -->
              <v-expand-transition>
                <div
                  v-if="candidates.reservationBypassed"
                  class="mb-5 pa-4 rounded-xl"
                  style="background: rgba(255, 152, 0, 0.05); border: 1px solid rgba(255, 152, 0, 0.2) !important">
                  <div class="d-flex align-start" style="gap: 10px">
                    <v-icon color="orange" class="mt-0.5">mdi-alert-decagram-outline</v-icon>
                    <div>
                      <div
                        class="text-caption font-weight-bold orange--text text--lighten-2"
                        style="font-size: 0.72rem !important; letter-spacing: 0.5px">
                        STAMMSCHICHT-KONFLIKT
                      </div>
                      <div class="text-caption grey--text text--lighten-2 mt-1" style="font-size: 0.72rem !important; line-height: 1.4">
                        Diese Schicht ist als fixe Stammschicht für <strong>{{ candidates.reservedEmployeeName }}</strong> hinterlegt. Sie ist heute
                        jedoch abwesend oder anderweitig verplant (Grund:
                        <span class="orange--text text--lighten-3 font-weight-semibold">{{
                          getConflictGerman(candidates.reservedEmployeeConflict)
                        }}</span
                        >).
                      </div>
                    </div>
                  </div>
                </div>
              </v-expand-transition>

              <!-- 1. Top Recommendation -->
              <div v-if="candidates.available && candidates.available.length > 0" class="mb-6">
                <div class="section-title text-caption font-weight-bold mb-2">EMPFEHLUNG DES ALGORITHMUS</div>
                <v-card
                  outlined
                  dark
                  class="reco-card rounded-xl pa-4"
                  :style="
                    candidates.available[0].hasPreferredPartner
                      ? 'border: 1px solid #009688 !important; box-shadow: 0 0 12px rgba(0, 150, 136, 0.25) !important;'
                      : ''
                  "
                  @click="assignEmployee(candidates.available[0]._id)"
                  draggable="true"
                  @dragstart="onCandidateDragStart($event, candidates.available[0])"
                  @dragend="draggedEmployee = null">
                  <div class="d-flex align-center mb-3">
                    <v-menu
                      open-on-hover
                      :close-on-content-click="false"
                      offset-y
                      bottom
                      min-width="320px"
                      dark
                      rounded="xl"
                      content-class="premium-hover-menu">
                      <template v-slot:activator="{ on, attrs }">
                        <div class="d-flex align-center flex-grow-1" style="min-width: 0">
                          <v-avatar v-on="on" v-bind="attrs" size="40" class="mr-2 border-reco cursor-pointer" style="flex-shrink: 0">
                            <img
                              :src="
                                candidates.available[0].picture ? '/img/profilbilder' + candidates.available[0].picture : '/img/default-avatar.png'
                              " />
                          </v-avatar>
                          <div class="flex-grow-1" style="min-width: 0">
                            <div
                              class="text-subtitle-2 font-weight-bold green--text text--lighten-3 truncate-text d-flex align-center"
                              style="font-size: 0.95rem !important">
                              {{ candidates.available[0].fullName }}
                              <v-chip
                                x-small
                                v-if="candidates.available[0].hasPreferredPartner"
                                color="teal"
                                class="ml-2 font-weight-bold white--text"
                                style="height: 16px; font-size: 0.65rem"
                                title="Bevorzugter Teampartner ist am selben Tag eingeteilt">
                                <v-icon x-small left color="white">mdi-account-heart</v-icon> Team-Präferenz
                              </v-chip>
                            </div>
                            <div
                              v-if="candidates.available[0].alreadyWorking"
                              class="orange--text text--lighten-2 text-caption font-weight-bold mt-1"
                              style="font-size: 0.72rem !important; line-height: 1.2">
                              <v-icon x-small color="orange" class="mr-1">mdi-clock-outline</v-icon>
                              Arbeitet bereits:
                              <div
                                v-for="(w, idx) in candidates.available[0].alreadyWorking"
                                :key="idx"
                                style="font-size: 0.68rem; padding-left: 14px"
                                class="grey--text text--lighten-1">
                                {{ w.time }} ({{ w.studio }})
                              </div>
                            </div>
                            <div
                              v-if="candidates.available[0].partialSickNote"
                              class="red--text text--lighten-2 text-caption font-weight-bold mt-1"
                              style="font-size: 0.72rem !important; line-height: 1.2">
                              <v-icon x-small color="red" class="mr-1">mdi-emoticon-sick-outline</v-icon>
                              {{ candidates.available[0].partialSickNote }}
                            </div>
                            <div class="d-flex align-center mt-1">
                              <span class="text-caption grey--text mr-2">Eignung:</span>
                              <strong class="green--text mr-2" style="font-size: 0.8rem"
                                >{{ getScorePercentage(candidates.available[0].score) }}%</strong
                              >
                              <v-progress-linear
                                color="green accent-3"
                                height="4"
                                rounded
                                :value="getScorePercentage(candidates.available[0].score)"
                                style="width: 50px"
                                class="d-inline-block"></v-progress-linear>
                            </div>
                          </div>
                        </div>
                      </template>

                      <shift-management-employee-planning-hover
                        v-if="getEmployeePlanningOverview(candidates.available[0])"
                        :overview="getEmployeePlanningOverview(candidates.available[0])"
                        :picture="getEmployeePicture(candidates.available[0])"
                        :is-dark-mode="isDarkMode" />
                    </v-menu>
                    <div class="d-flex align-center flex-column align-end">
                      <v-chip
                        v-if="candidates.reservedEmployeeName === candidates.available[0].fullName"
                        color="indigo"
                        small
                        class="font-weight-bold mb-1 white--text">
                        <v-icon x-small left color="white">mdi-pin</v-icon> Stammschicht
                      </v-chip>
                      <v-chip v-else color="green darken-2" small class="font-weight-bold mb-1">Beste Wahl</v-chip>
                      <v-btn icon x-small color="amber" @click.stop="openNoteDialog(candidates.available[0], selectedShift.date)" title="Tagesnotiz">
                        <v-icon x-small>mdi-note-edit-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <v-divider dark class="my-2" style="border-color: rgba(255, 255, 255, 0.06)"></v-divider>
                  <div class="d-flex justify-space-between text-caption px-1">
                    <div>
                      Soll: <strong>{{ getCandidateStudioTargetHours(candidates.available[0]).toFixed(0) }}h</strong>
                    </div>
                    <div>
                      Plan: <strong>{{ getCandidateStudioPlannedHours(candidates.available[0]).toFixed(0) }}h</strong>
                    </div>
                    <div>
                      <strong :class="getStreakColor(getConsecutiveStreak(candidates.available[0], selectedShift.date) + 1)">{{
                        getStreakDisplayText(candidates.available[0], selectedShift.date)
                      }}</strong>
                    </div>
                  </div>
                  <!-- 0% Suitability explanation for top recommendation -->
                  <div
                    v-if="getScorePercentage(candidates.available[0].score) === 0 || hasActiveWarnings(candidates.available[0])"
                    class="orange--text text--lighten-2 text-caption mt-2 px-1 d-flex align-start"
                    style="font-size: 0.72rem !important; letter-spacing: 0.1px; line-height: 1.3">
                    <v-icon x-small color="orange" class="mr-1 mt-0.5">mdi-alert-circle-outline</v-icon>
                    <span>Hinweis: {{ getActiveWarningsText(candidates.available[0]) }}</span>
                  </div>
                  <!-- Roody Insight Section -->
                  <div
                    v-if="getRecommendationReasons(candidates.available[0]).length > 0"
                    class="roody-insight-box mt-3 pa-2 rounded-lg"
                    style="background: rgba(102, 187, 106, 0.05); border: 1px solid rgba(102, 187, 106, 0.15)">
                    <div
                      class="d-flex align-center text-caption font-weight-bold green--text text--lighten-3 mb-1"
                      style="font-size: 0.68rem !important; letter-spacing: 0.2px">
                      <v-icon x-small color="green accent-3" class="mr-1">mdi-brain</v-icon>
                      ROODY-Empfehlungsgrund:
                    </div>
                    <div class="d-flex flex-column text-caption white--text" style="gap: 4px; font-size: 0.72rem !important; line-height: 1.3">
                      <div v-for="(reason, rIdx) in getRecommendationReasons(candidates.available[0])" :key="rIdx" class="d-flex align-start">
                        <v-icon x-small color="green accent-3" class="mr-1.5 mt-0.5">mdi-check-circle-outline</v-icon>
                        <span>{{ reason }}</span>
                      </div>
                    </div>
                  </div>
                  <v-btn small depressed color="green darken-3" block class="mt-3 font-weight-bold text-none rounded-lg">
                    Diese Empfehlung eintragen
                  </v-btn>
                </v-card>
              </div>

              <!-- 2. Other Available Employees -->
              <div class="mb-6">
                <div class="section-title text-caption font-weight-bold mb-2">
                  VERFÜGBARE MITARBEITER ({{ candidates.available ? Math.max(0, candidates.available.length - 1) : 0 }})
                </div>
                <div
                  v-if="!candidates.available || candidates.available.length <= 1"
                  class="text-caption grey--text pa-3 text-center rounded-lg border-dashed">
                  Keine weiteren direkt verfügbaren Mitarbeiter gefunden.
                </div>
                <v-list dense dark class="pa-0 grey darken-4">
                  <div v-for="(emp, i) in candidates.available" :key="emp._id">
                    <!-- Skip the first recommendation -->
                    <v-list-item
                      v-if="i > 0"
                      class="pa-3 mb-2 rounded-xl grey darken-3 hover-row align-center d-flex"
                      :style="emp.hasPreferredPartner ? 'border: 1px solid rgba(0, 150, 136, 0.4) !important;' : ''"
                      @click="assignEmployee(emp._id)"
                      draggable="true"
                      @dragstart="onCandidateDragStart($event, emp)"
                      @dragend="draggedEmployee = null"
                      @dblclick="assignEmployee(emp._id)">
                      <v-menu
                        open-on-hover
                        :close-on-content-click="false"
                        offset-y
                        bottom
                        min-width="320px"
                        dark
                        rounded="xl"
                        content-class="premium-hover-menu">
                        <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                          <div class="d-flex align-center flex-grow-1">
                            <v-avatar v-on="menuOn" v-bind="menuAttrs" size="36" class="mr-3 cursor-pointer">
                              <img :src="emp.picture ? '/img/profilbilder' + emp.picture : '/img/default-avatar.png'" />
                            </v-avatar>
                            <div class="flex-grow-1">
                              <div class="text-subtitle-2 font-weight-bold white--text d-flex align-center">
                                {{ emp.fullName }}
                                <v-chip
                                  x-small
                                  v-if="emp.hasPreferredPartner"
                                  color="teal"
                                  class="ml-2 font-weight-bold white--text"
                                  style="height: 16px; font-size: 0.65rem"
                                  title="Bevorzugter Teampartner ist am selben Tag eingeteilt">
                                  <v-icon x-small left color="white">mdi-account-heart</v-icon> Team-Präferenz
                                </v-chip>
                                <v-chip
                                  x-small
                                  v-if="candidates.reservedEmployeeName === emp.fullName"
                                  color="indigo"
                                  class="ml-2 font-weight-bold white--text">
                                  <v-icon x-small left color="white">mdi-pin</v-icon> Stammschicht
                                </v-chip>
                                <v-tooltip bottom v-if="emp.hasMatchingWish">
                                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                    <v-icon x-small color="pink" class="ml-2" v-bind="tooltipAttrs" v-on="tooltipOn">mdi-heart</v-icon>
                                  </template>
                                  <span>Hat Schichtwunsch für diesen Tag</span>
                                </v-tooltip>
                              </div>
                              <div class="text-caption grey--text">
                                Soll: {{ getCandidateStudioTargetHours(emp).toFixed(0) }}h · Plan:
                                {{ getCandidateStudioPlannedHours(emp).toFixed(0) }}h
                              </div>
                              <div
                                v-if="emp.alreadyWorking"
                                class="orange--text text--lighten-2 text-caption font-weight-bold mt-1"
                                style="font-size: 0.72rem !important; line-height: 1.2">
                                <v-icon x-small color="orange" class="mr-1">mdi-clock-outline</v-icon>
                                Arbeitet bereits:
                                <div
                                  v-for="(w, idx) in emp.alreadyWorking"
                                  :key="idx"
                                  style="font-size: 0.68rem; padding-left: 14px"
                                  class="grey--text text--lighten-1">
                                  {{ w.time }} ({{ w.studio }})
                                </div>
                              </div>
                              <div
                                v-if="emp.partialSickNote"
                                class="red--text text--lighten-2 text-caption font-weight-bold mt-1"
                                style="font-size: 0.72rem !important; line-height: 1.2">
                                <v-icon x-small color="red" class="mr-1">mdi-emoticon-sick-outline</v-icon>
                                {{ emp.partialSickNote }}
                              </div>
                              <div
                                v-if="getScorePercentage(emp.score) === 0 || hasActiveWarnings(emp)"
                                class="orange--text text--lighten-2 text-caption mt-0.5 d-flex align-center"
                                style="font-size: 0.68rem !important; line-height: 1.2">
                                <v-icon x-small color="orange" class="mr-1">mdi-alert-circle-outline</v-icon>
                                <span>Hinweis: {{ getActiveWarningsText(emp) }}</span>
                              </div>
                            </div>
                          </div>
                        </template>

                        <shift-management-employee-planning-hover
                          v-if="getEmployeePlanningOverview(emp)"
                          :overview="getEmployeePlanningOverview(emp)"
                          :picture="getEmployeePicture(emp)"
                          :is-dark-mode="isDarkMode" />
                      </v-menu>
                      <div class="text-right d-flex flex-column align-end">
                        <div class="d-flex align-center">
                          <v-btn
                            icon
                            x-small
                            color="amber"
                            class="mr-1 hover-show"
                            @click.stop="openNoteDialog(emp, selectedShift.date)"
                            title="Tagesnotiz">
                            <v-icon x-small>mdi-note-edit-outline</v-icon>
                          </v-btn>
                          <v-tooltip bottom :dark="isDarkMode" :light="!isDarkMode" rounded="xl" max-width="320px" content-class="premium-hover-menu">
                            <template v-slot:activator="{ on, attrs }">
                              <v-chip
                                v-on="on"
                                v-bind="attrs"
                                x-small
                                :color="getScorePercentage(emp.score) === 0 ? 'orange darken-4' : 'green darken-3'"
                                class="font-weight-bold mb-1 white--text">
                                {{ getScorePercentage(emp.score) }}% Eignung
                              </v-chip>
                            </template>
                            <div class="pa-2">
                              <div class="text-caption font-weight-bold mb-1 white--text">ROODY-Empfehlungsgründe:</div>
                              <div v-if="getRecommendationReasons(emp).length > 0">
                                <div
                                  v-for="(reason, idx) in getRecommendationReasons(emp)"
                                  :key="idx"
                                  class="d-flex align-start text-caption py-0.5"
                                  style="font-size: 0.72rem !important; line-height: 1.3">
                                  <v-icon x-small color="green accent-3" class="mr-1.5 mt-0.5">mdi-check-circle-outline</v-icon>
                                  <span class="white--text">{{ reason }}</span>
                                </div>
                              </div>
                              <div v-else class="text-caption text-italic grey--text">Bietet die fairste Rotation im Team.</div>
                            </div>
                          </v-tooltip>
                        </div>
                        <span
                          class="text-caption font-weight-semibold"
                          :class="getStreakColor(getConsecutiveStreak(emp, selectedShift.date) + 1)"
                          style="font-size: 0.7rem">
                          {{ getStreakDisplayText(emp, selectedShift.date) }}
                        </span>
                      </div>
                    </v-list-item>
                  </div>
                </v-list>
              </div>

              <!-- 3. Unavailable Employees with Override -->
              <div>
                <div class="section-title text-caption font-weight-bold mb-2">
                  NICHT VERFÜGBAR ({{ candidates.unavailable ? candidates.unavailable.length : 0 }})
                </div>
                <v-list dense dark class="pa-0 grey darken-4">
                  <div
                    v-for="emp in candidates.unavailable"
                    :key="emp._id"
                    class="pa-3 mb-3 rounded-xl grey darken-3 border-danger-left d-flex flex-column"
                    style="background: rgba(255, 255, 255, 0.02); border: 1px solid rgba(255, 255, 255, 0.04); position: relative">
                    <!-- Top Row: Avatar, Name and Action Button -->
                    <div class="d-flex align-center justify-space-between w-100 mb-2">
                      <div class="d-flex align-center">
                        <v-menu
                          open-on-hover
                          :close-on-content-click="false"
                          offset-y
                          bottom
                          min-width="320px"
                          dark
                          rounded="xl"
                          content-class="premium-hover-menu">
                          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                            <div class="d-flex align-center">
                              <v-avatar v-on="menuOn" v-bind="menuAttrs" size="32" class="mr-2 opacity-semi border-danger cursor-pointer">
                                <img :src="emp.picture ? '/img/profilbilder' + emp.picture : '/img/default-avatar.png'" />
                              </v-avatar>
                              <div
                                class="text-subtitle-2 font-weight-bold white--text opacity-semi truncate-text"
                                style="max-width: 120px"
                                :title="emp.fullName">
                                {{ emp.fullName }}
                              </div>
                            </div>
                          </template>

                          <shift-management-employee-planning-hover
                            v-if="getEmployeePlanningOverview(emp)"
                            :overview="getEmployeePlanningOverview(emp)"
                            :picture="getEmployeePicture(emp)"
                            :is-dark-mode="isDarkMode" />
                        </v-menu>
                      </div>

                      <v-btn
                        x-small
                        depressed
                        color="orange darken-4"
                        class="font-weight-bold text-caption text-none rounded-lg"
                        @click="openOverrideDialog(emp)">
                        Trotzdem eintragen
                      </v-btn>
                    </div>

                    <!-- Bottom Row: Clean text-based list of rejection reasons -->
                    <div class="d-flex flex-column pl-1" style="gap: 4px">
                      <div v-for="(reason, ri) in emp.reasons" :key="ri" class="d-flex align-start text-caption red--text text--lighten-2">
                        <v-icon x-small color="red lighten-1" class="mr-1 mt-0.5">mdi-close-octagon-outline</v-icon>
                        <span style="font-size: 0.72rem; line-height: 1.3">{{ reason }}</span>
                      </div>
                    </div>
                  </div>
                </v-list>
                <div v-if="!candidates.unavailable || candidates.unavailable.length === 0" class="text-caption grey--text text-center py-2">
                  Keine gesperrten Mitarbeiter für diese Schicht.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Override Dialogue Popup -->
    <v-dialog v-model="overrideReasonDialog" max-width="450px" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :dark="isDarkMode" :light="!isDarkMode" class="rounded-xl pa-4" :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="orange">mdi-alert-decagram</v-icon>
          <span style="font-size: 1.1rem">Mitarbeiter trotzdem zuweisen?</span>
        </v-card-title>

        <v-card-text class="pt-2">
          <div
            v-if="selectedOverrideEmployee"
            class="mb-4 pa-3 rounded-lg d-flex align-center"
            :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'">
            <v-avatar size="32" class="mr-3">
              <img :src="selectedOverrideEmployee.picture ? '/img/profilbilder' + selectedOverrideEmployee.picture : '/img/default-avatar.png'" />
            </v-avatar>
            <div>
              <strong :class="isDarkMode ? 'white--text' : 'black--text'">{{ selectedOverrideEmployee.fullName }}</strong>
              <div class="text-caption red--text text--lighten-2 mt-1">
                Gesperrt wegen: {{ selectedOverrideEmployee.reasons ? selectedOverrideEmployee.reasons.join(", ") : "" }}
              </div>
            </div>
          </div>

          <div :class="isDarkMode ? 'white--text' : 'black--text'" class="text-body-2 mb-2">
            Möchtest du den Mitarbeiter dieser Schicht trotzdem manuell zuweisen?
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="overrideReasonDialog = false">Abbrechen</v-btn>
          <v-btn color="orange" depressed @click="submitOverride" class="font-weight-bold text-none rounded-lg"> Ja, Zuweisen </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for partial sickness warning and configuration -->
    <v-dialog v-model="sickAssignDialog" max-width="500px" :dark="isDarkMode" :light="!isDarkMode">
      <v-card :dark="isDarkMode" :light="!isDarkMode" class="rounded-xl pa-4" :class="isDarkMode ? 'grey darken-3' : 'white'">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="orange">mdi-alert</v-icon>
          <span style="font-size: 1.1rem" :class="isDarkMode ? 'white--text' : 'black--text'">Mitarbeiter ist teilweise krank / abwesend</span>
        </v-card-title>

        <v-card-text class="pt-2">
          <div v-if="sickAssignEmployee" class="mb-4 pa-3 rounded-lg d-flex align-center" :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'">
            <v-avatar size="32" class="mr-3">
              <img :src="sickAssignEmployee.picture ? '/img/profilbilder' + sickAssignEmployee.picture : '/img/default-avatar.png'" />
            </v-avatar>
            <div>
              <strong :class="isDarkMode ? 'white--text' : 'black--text'">{{ sickAssignEmployee.fullName }}</strong>
              <div class="text-caption red--text text--lighten-2 mt-1">
                {{ sickAssignDetails ? sickAssignDetails.sickPercentage : 0 }}% krank / abwesend (Soll:
                {{ sickAssignDetails ? sickAssignDetails.shiftInfoTotalHours : 0 }}h, Krank: {{ sickAssignDetails ? sickAssignDetails.hours : 0 }}h)
              </div>
            </div>
          </div>

          <div :class="isDarkMode ? 'white--text' : 'black--text'" class="text-body-2 mb-4">
            Bitte wähle aus, wie diese Schicht im Dienstplan verrechnet werden soll:
          </div>

          <v-radio-group v-model="sickAssignCalculationMode" class="mt-0" hide-details>
            <v-radio
              value="fixed"
              label="Schicht bleibt aktiv (Dienstplan nicht ändern)"
              color="primary"
              :dark="isDarkMode"
              :light="!isDarkMode"></v-radio>
            <div class="text-caption grey--text pl-8 mb-3" style="line-height: 1.2">
              Der Mitarbeiter bleibt voll im Dienstplan eingeteilt (keine Änderung der Schicht). Die Krankheitsstunden werden im Zeiterfasser als
              Guthaben gutgeschrieben.
            </div>

            <v-radio
              value="proportional"
              label="Schicht anpassen (anteilig im Dienstplan kürzen)"
              color="primary"
              :dark="isDarkMode"
              :light="!isDarkMode"></v-radio>
            <div class="text-caption grey--text pl-8 mb-3" style="line-height: 1.2">
              Die Schicht wird im Kalender verkürzt (splittet die Schicht in einen kranken und einen gesunden Teil).
            </div>
          </v-radio-group>

          <v-expand-transition>
            <div v-if="sickAssignCalculationMode === 'proportional'" class="mt-3 pl-8">
              <v-select
                label="Lage der Kürzung"
                v-model="sickAssignLocation"
                :items="[
                  { value: 'start', text: 'Am Anfang der Schicht' },
                  { value: 'end', text: 'Am Ende der Schicht' },
                ]"
                outlined
                dense
                hide-details
                class="rounded-lg"
                :dark="isDarkMode"
                :light="!isDarkMode"></v-select>
            </div>
          </v-expand-transition>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="sickAssignDialog = false">Abbrechen</v-btn>
          <v-btn color="primary" depressed @click="submitSickAssign" class="font-weight-bold text-none rounded-lg"> Zuweisen & Speichern </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Finish Warning Dialog -->
    <v-dialog v-model="finishWarningDialog" max-width="500px" dark>
      <v-card dark class="rounded-xl pa-4 grey darken-3">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          <span style="font-size: 1.1rem">Planansicht öffnen</span>
        </v-card-title>

        <v-card-text class="pt-2">
          <p class="text-body-2 white--text">
            Es gibt noch <strong>{{ unassignedCountInMonth }} unbesetzte Schichten</strong> in diesem Monat.
          </p>
          <p class="text-caption grey--text">
            Wenn du diese Schichten löschst, gilt der Dienstplan in der Monatsübersicht als zu 100% fertig geplant.
          </p>
        </v-card-text>

        <v-card-actions class="d-flex flex-column" style="gap: 8px">
          <v-btn
            color="warning"
            depressed
            block
            @click="deleteUnassignedAndFinish"
            :loading="deletingUnassigned"
            class="font-weight-bold text-none rounded-lg black--text">
            Offene Schichten löschen & Plan prüfen
          </v-btn>
          <div class="d-flex w-100" style="gap: 8px">
            <v-btn text class="text-none flex-grow-1" @click="finishWarningDialog = false">Abbrechen</v-btn>
            <v-btn color="indigo" depressed class="font-weight-bold text-none rounded-lg flex-grow-1" @click="$emit('view-plan')">
              Mit offenen Schichten zur Planansicht
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Warning Dialog for Resetting Week Assignments -->
    <v-dialog v-model="confirmResetWeekDialog" max-width="480px" dark>
      <v-card dark class="rounded-xl pa-4 grey darken-3">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="red">mdi-alert-circle</v-icon>
          <span style="font-size: 1.1rem">Woche zurücksetzen?</span>
        </v-card-title>
        <v-card-text class="pt-2">
          Möchtest du wirklich alle Zuweisungen für die aktuelle Woche ({{ currentWeek ? currentWeek.label : "" }}) aufheben? Dabei werden alle
          zugeordneten Mitarbeiter wieder entfernt und die Schichten werden unbesetzt. Spezialschichten bleiben erhalten.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmResetWeekDialog = false">Abbrechen</v-btn>
          <v-btn color="red" depressed @click="resetWeekPlanning" class="font-weight-bold text-none rounded-lg mr-2" :loading="resettingWeek">
            Ja, zurücksetzen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Spezialtag Konfigurator Dialogue -->
    <v-dialog v-model="specialDayDialog" fullscreen transition="dialog-bottom-transition" dark hide-overlay>
      <v-card class="grey darken-4 d-flex flex-column" style="height: 100vh">
        <v-toolbar color="grey darken-4" elevation="1" class="border-bottom-subtle flex-grow-0" dark>
          <v-btn icon @click="specialDayDialog = false" class="mr-2"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title class="font-weight-bold pl-0 white--text">
            <span class="blue--text text--accent-2 mr-2">Spezialtag</span> Konfigurator
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <span class="text-caption grey--text mr-4 d-none d-sm-block" v-if="specialDayDate"> {{ specialDayShifts.length }} Schichten </span>
            <v-btn color="success" depressed @click="saveSpecialDay" :loading="savingSpecialDay" :disabled="!specialDayDate">
              <v-icon left>mdi-content-save-check</v-icon> Speichern
            </v-btn>
          </div>
        </v-toolbar>

        <v-card-text class="pa-0 flex-grow-1 d-flex grey darken-4" style="overflow: hidden">
          <v-row no-gutters class="fill-height">
            <!-- Left Info Column -->
            <v-col cols="12" md="3" lg="2" class="border-right-subtle d-flex flex-column grey darken-4 fill-height pa-4" style="overflow-y: auto">
              <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2 text-uppercase">1. Wann?</div>
              <v-text-field
                :value="$moment(specialDayDate).format('dddd, DD.MM.YYYY')"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                class="rounded-lg mb-6 font-weight-bold white--text"
                background-color="grey darken-3"></v-text-field>

              <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2 text-uppercase">2. Modus</div>
              <v-item-group v-model="specialDayMode" mandatory class="d-flex flex-column">
                <v-item v-slot="{ active, toggle }" value="replace">
                  <v-card
                    flat
                    class="mb-3 pa-3 cursor-pointer transition-swing"
                    :class="active ? 'blue darken-4 border-blue' : 'grey darken-3 border-subtle'"
                    @click="toggle">
                    <div class="d-flex align-center mb-1">
                      <v-icon small :color="active ? 'blue accent-2' : 'grey'" class="mr-2">mdi-refresh</v-icon>
                      <div class="font-weight-bold text-body-2" :class="active ? 'white--text' : 'grey--text'">Ersetzen (Override)</div>
                    </div>
                    <div class="text-caption" :class="active ? 'blue--text text--lighten-4' : 'grey--text text--darken-1'" style="line-height: 1.2">
                      Löscht die Standardschichten dieses Tages und setzt nur die hier konfigurierten Schichten ein.
                    </div>
                  </v-card>
                </v-item>

                <v-item v-slot="{ active, toggle }" value="add">
                  <v-card
                    flat
                    class="pa-3 cursor-pointer transition-swing"
                    :class="active ? 'green darken-4 border-green' : 'grey darken-3 border-subtle'"
                    @click="toggle">
                    <div class="d-flex align-center mb-1">
                      <v-icon small :color="active ? 'green accent-3' : 'grey'" class="mr-2">mdi-plus-circle</v-icon>
                      <div class="font-weight-bold text-body-2" :class="active ? 'white--text' : 'grey--text'">Zusatz (Add-On)</div>
                    </div>
                    <div class="text-caption" :class="active ? 'green--text text--lighten-4' : 'grey--text text--darken-1'" style="line-height: 1.2">
                      Behält die Standardschichten bei und fügt diese Schichten zusätzlich hinzu.
                    </div>
                  </v-card>
                </v-item>
              </v-item-group>

              <v-card flat class="mt-8 pa-4 grey darken-3 rounded-lg border-subtle">
                <div class="d-flex justify-space-between mb-2 text-caption grey--text">
                  <span>Schichten:</span>
                  <strong class="white--text">{{ specialDayShifts.length }}</strong>
                </div>
                <v-divider class="my-2 grey darken-2"></v-divider>
                <div v-if="specialDayMode === 'add'" class="text-caption green--text text--darken-1">+ Standard-Schichten bleiben aktiv</div>
              </v-card>
            </v-col>

            <!-- Right Workspace Column -->
            <v-col cols="12" md="9" lg="10" class="fill-height grey darken-4 d-flex flex-column" style="position: relative; overflow: hidden">
              <div class="px-6 py-4 border-bottom-subtle d-flex justify-space-between align-center bg-stripes flex-shrink-0">
                <div>
                  <div class="text-h6 font-weight-bold white--text d-flex align-center">
                    {{ $moment(specialDayDate).format("dddd, DD. MMMM YYYY") }}
                    <v-chip
                      x-small
                      class="ml-3 font-weight-bold white--text"
                      :color="specialDayMode === 'replace' ? 'blue darken-3' : 'green darken-3'">
                      {{ specialDayMode === "replace" ? "OVERRIDE MODUS" : "ADD-ON MODUS" }}
                    </v-chip>
                  </div>
                  <div class="text-caption grey--text mt-1">
                    {{
                      specialDayMode === "replace"
                        ? "Bestehende Schichten werden durch die untenstehenden ersetzt."
                        : "Die untenstehenden Schichten werden zusätzlich geplant."
                    }}
                  </div>
                </div>
                <v-btn color="primary" depressed @click="addSpecialDayShift" class="px-4"> <v-icon left>mdi-plus</v-icon> Schicht hinzufügen </v-btn>
              </div>

              <!-- Shifts list -->
              <div class="flex-grow-1 overflow-y-auto pa-6 custom-scrollbar" style="max-height: calc(100vh - 140px)">
                <!-- Warning standard shifts in Add mode -->
                <v-expand-transition>
                  <div v-if="specialDayMode === 'add' && standardShiftsForDate.length > 0" class="mb-6">
                    <div class="text-overline grey--text text--darken-1 mb-2 ml-1 d-flex align-center">
                      <v-icon x-small left color="grey darken-1" class="mr-1">mdi-lock-outline</v-icon>
                      Standard-Schichten (bleiben erhalten)
                    </div>
                    <v-row dense>
                      <v-col v-for="(std, idx) in standardShiftsForDate" :key="'std' + idx" cols="12" sm="6" md="4" lg="3">
                        <v-card
                          flat
                          outlined
                          class="grey darken-3 border-subtle pa-2 d-flex align-center opacity-60"
                          style="background: rgba(255, 255, 255, 0.01)">
                          <v-chip x-small color="grey darken-2" class="mr-2 white--text font-weight-bold">{{ getShiftTimeRange(std) }}</v-chip>
                          <span class="text-caption grey--text text--lighten-2 text-truncate">
                            {{ getShiftName(std.shiftTemplateId) }}
                          </span>
                        </v-card>
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>

                <!-- Empty state -->
                <div v-if="specialDayShifts.length === 0" class="d-flex flex-column align-center justify-center pa-10 border-dashed rounded-lg mt-4">
                  <v-icon size="48" color="grey darken-2" class="mb-2">mdi-playlist-plus</v-icon>
                  <div class="grey--text text--darken-1">Keine speziellen Schichten für diesen Tag eingetragen.</div>
                  <v-btn
                    v-if="specialDayMode === 'replace'"
                    text
                    color="blue accent-2"
                    class="mt-2 text-none"
                    small
                    @click="loadSpecialDayStandardShifts">
                    Standard-Vorlage laden
                  </v-btn>
                </div>

                <!-- Custom shift cards -->
                <v-slide-y-transition group>
                  <v-card
                    v-for="(shift, i) in specialDayShifts"
                    :key="'sh' + i"
                    outlined
                    class="mb-4 shift-card grey darken-3 elevation-2 rounded-xl border-subtle">
                    <div
                      class="px-4 py-2 d-flex align-center justify-space-between grey darken-4 border-bottom flex-shrink-0"
                      style="min-height: 48px">
                      <div class="d-flex align-center flex-grow-1">
                        <v-icon small color="grey" class="mr-2">mdi-clock-outline</v-icon>
                        <div style="width: 280px">
                          <v-select
                            :items="availableShifts"
                            v-model="shift.shiftTemplateId"
                            placeholder="Schicht-Vorlage wählen (optional)"
                            item-text="name"
                            item-value="_id"
                            dense
                            flat
                            solo-inverted
                            hide-details
                            background-color="grey darken-2"
                            class="small-select font-weight-bold"
                            @change="(val) => applySpecialDayTemplate(shift, val)">
                            <template v-slot:item="{ item }">
                              <v-chip x-small :color="item.color" class="mr-2" label></v-chip> {{ item.name }}
                            </template>
                            <template v-slot:selection="{ item }">
                              <v-chip x-small :color="item.color" class="mr-2" label></v-chip> {{ item.name }}
                            </template>
                          </v-select>
                        </div>
                      </div>

                      <div class="d-flex">
                        <v-btn icon small color="grey" @click="copySpecialDayShift(shift)" title="Duplizieren">
                          <v-icon small>mdi-content-copy</v-icon>
                        </v-btn>
                        <v-btn icon small color="red lighten-2" @click="removeSpecialDayShift(i)" title="Löschen">
                          <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <div class="pa-4">
                      <v-row>
                        <v-col cols="12" md="6" class="border-right-responsive pr-md-6">
                          <div class="d-flex justify-space-between align-center mb-3">
                            <span class="text-caption font-weight-bold grey--text text--lighten-1 text-uppercase">Zeitblöcke & Pausen</span>
                            <v-btn x-small text color="blue accent-2" @click="addSpecialDayTimeBlock(shift)">
                              <v-icon left x-small>mdi-plus</v-icon> Block
                            </v-btn>
                          </div>

                          <div v-for="(time, tIndex) in shift.times" :key="'t' + tIndex" class="d-flex align-center mb-2">
                            <v-menu offset-y>
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                  v-bind="attrs"
                                  v-on="on"
                                  small
                                  label
                                  :color="time.type === 'Arbeitszeit' ? 'blue darken-3' : 'grey darken-2'"
                                  class="mr-3"
                                  style="width: 80px; justify-content: center; font-weight: bold">
                                  {{ time.type === "Arbeitszeit" ? "Arbeit" : "Pause" }}
                                  <v-icon right x-small>mdi-chevron-down</v-icon>
                                </v-chip>
                              </template>
                              <v-list dense dark color="grey darken-3">
                                <v-list-item @click="time.type = 'Arbeitszeit'">
                                  <v-list-item-title class="blue--text">Arbeit</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="time.type = 'Pause'">
                                  <v-list-item-title class="grey--text">Pause</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>

                            <div
                              class="d-flex align-center bg-grey-darken-4 rounded-lg px-2 py-1 border-subtle"
                              style="background: rgba(0, 0, 0, 0.25)">
                              <input
                                type="time"
                                v-model="time.start"
                                class="time-input white--text"
                                style="
                                  background: transparent;
                                  border: none;
                                  font-family: monospace;
                                  outline: none;
                                  width: 85px;
                                  text-align: center;
                                " />
                              <span class="grey--text mx-2">-</span>
                              <input
                                type="time"
                                v-model="time.end"
                                class="time-input white--text"
                                style="
                                  background: transparent;
                                  border: none;
                                  font-family: monospace;
                                  outline: none;
                                  width: 85px;
                                  text-align: center;
                                " />
                            </div>

                            <v-btn
                              icon
                              x-small
                              color="grey"
                              class="ml-2"
                              @click="removeSpecialDayTimeBlock(shift, tIndex)"
                              :disabled="shift.times.length === 1">
                              <v-icon small>mdi-close</v-icon>
                            </v-btn>
                          </div>
                        </v-col>

                        <v-col cols="12" md="6" class="pl-md-6">
                          <div class="text-caption font-weight-bold grey--text text--lighten-1 text-uppercase mb-3">Zuweisung & Info</div>

                          <v-autocomplete
                            v-model="shift.assignedUserId"
                            :items="employees"
                            item-text="fullName"
                            :item-value="getEmployeeUserId"
                            label="Mitarbeiter fixieren (optional)"
                            dense
                            outlined
                            flat
                            hide-details
                            class="mb-3 rounded-lg"
                            background-color="grey darken-4"
                            placeholder="Automatisch eintragen lassen"
                            clearable>
                            <template v-slot:prepend-inner>
                              <v-icon small color="grey" class="mt-1">mdi-account-outline</v-icon>
                            </template>
                            <template v-slot:item="{ item }">
                              <v-avatar size="24" class="mr-3">
                                <img :src="getEmployeePicture(item)" />
                              </v-avatar>
                              <span :class="{ 'red--text text--lighten-1': getEmployeeStatus(item, specialDayDate).length > 0 }">
                                {{ item.fullName }}
                                <span v-if="getEmployeeStatus(item, specialDayDate).length > 0" class="font-weight-bold ml-1 text-caption grey--text">
                                  [{{ getEmployeeStatus(item, specialDayDate).join(", ") }}]
                                </span>
                              </span>
                            </template>
                            <template v-slot:selection="{ item }">
                              <v-chip
                                x-small
                                :color="getEmployeeStatus(item, specialDayDate).length > 0 ? 'red lighten-1' : 'green darken-3'"
                                label
                                class="white--text font-weight-bold">
                                {{ item.fullName }}
                                <span v-if="getEmployeeStatus(item, specialDayDate).length > 0" class="ml-1 text-caption white--text opacity-80">
                                  ({{ getEmployeeStatus(item, specialDayDate).join(", ") }})
                                </span>
                              </v-chip>
                            </template>
                          </v-autocomplete>

                          <v-text-field
                            v-model="shift.note"
                            label="Notiz / Event-Name"
                            dense
                            outlined
                            flat
                            hide-details
                            background-color="grey darken-4"
                            class="rounded-lg"
                            placeholder="z.B. Feiertag / Sonderöffnungszeit">
                            <template v-slot:prepend-inner>
                              <v-icon small color="grey" class="mt-1">mdi-text-short</v-icon>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-card>
                </v-slide-y-transition>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add Shift Dialogue -->
    <v-dialog v-model="addShiftDialog" max-width="450px" dark>
      <v-card dark class="rounded-xl pa-4 grey darken-3">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="primary">mdi-calendar-plus</v-icon>
          <span style="font-size: 1.1rem">Manuelle Schicht hinzufügen</span>
        </v-card-title>

        <v-card-text class="pt-2">
          <div class="mb-4 text-caption grey--text">
            Fügen Sie für den <strong>{{ $moment(addShiftDate).format("dddd, DD.MM.YYYY") }}</strong> eine neue Schicht hinzu.
          </div>

          <v-select
            v-model="addShiftTemplateId"
            :items="availableShifts"
            item-text="name"
            item-value="_id"
            label="Schicht-Vorlage auswählen (Pflicht)"
            outlined
            dense
            dark
            class="mb-3"></v-select>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="addShiftDialog = false">Abbrechen</v-btn>
          <v-btn
            color="primary"
            depressed
            :disabled="!addShiftTemplateId"
            @click="submitAddShift"
            :loading="creatingShift"
            class="font-weight-bold text-none rounded-lg">
            Schicht hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Note Dialogue -->
    <v-dialog v-model="noteDialog" max-width="480px" dark>
      <v-card dark class="rounded-xl pa-4 grey darken-3">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="amber">mdi-note-text-outline</v-icon>
          <span style="font-size: 1.1rem">Notizen für Mitarbeiter</span>
        </v-card-title>

        <v-card-text class="pt-2">
          <div v-if="activeNoteEmployee" class="mb-4 pa-3 grey darken-4 rounded-lg d-flex align-center">
            <v-avatar size="32" class="mr-3">
              <img :src="getEmployeePicture(activeNoteEmployee)" />
            </v-avatar>
            <div>
              <strong class="white--text">{{ activeNoteEmployee.fullName }}</strong>
              <div class="text-caption grey--text mt-1">Datum: {{ $moment(activeNoteDate).format("dddd, DD.MM.YYYY") }}</div>
            </div>
          </div>

          <!-- List of existing notes -->
          <div class="mb-4">
            <div class="section-title text-caption font-weight-bold mb-2">EXISTIERENDE NOTIZEN</div>
            <div v-if="getActiveEmployeeNotes().length === 0" class="text-caption grey--text text-center py-4 border-dashed rounded-lg">
              Keine Notizen für diesen Tag vorhanden.
            </div>
            <v-list v-else dense dark class="pa-0 grey darken-4 rounded-lg" style="max-height: 180px; overflow-y: auto">
              <v-list-item v-for="note in getActiveEmployeeNotes()" :key="note._id" class="pa-2 border-bottom-dashed">
                <div class="flex-grow-1">
                  <div class="text-caption font-weight-bold white--text">
                    {{ note.author ? note.author.vorname + " " + note.author.nachname : "System" }}
                  </div>
                  <div class="text-body-2 grey--text text--lighten-2 mt-0.5">{{ note.content }}</div>
                </div>
                <v-btn icon x-small color="error" class="ml-2" @click="deleteNote(note._id)">
                  <v-icon x-small>mdi-trash-can-outline</v-icon>
                </v-btn>
              </v-list-item>
            </v-list>
          </div>

          <!-- Add new note -->
          <v-textarea v-model="newNoteText" label="Neue Notiz hinzufügen" outlined dense rows="3" dark hide-details class="mb-3"></v-textarea>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="noteDialog = false">Schliessen</v-btn>
          <v-btn
            color="amber"
            depressed
            :disabled="!newNoteText.trim()"
            @click="submitAddNote"
            :loading="savingNote"
            class="font-weight-bold text-none rounded-lg black--text">
            Notiz hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import ShiftManagementEmployeePlanningHover from "./ShiftManagementEmployeePlanningHover.vue"

export default {
  name: "ShiftManagementAssistant",
  components: {
    ShiftManagementEmployeePlanningHover,
  },
  props: {
    studioId: { type: String, required: true },
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    availableShifts: { type: Array, required: true },
    schoolHolidays: { type: Array, default: () => [] },
    publicHolidays: { type: Array, default: () => [] },
    employees: { type: Array, required: true },
    previousContext: { type: Object, default: null },
    isDarkMode: { type: Boolean, default: true },
    initialShiftId: { type: String, default: null },
  },
  data() {
    return {
      currentWeekIndex: 0,
      loading: true,
      autoFilling: false,
      planningStrategy: "rotation",
      shifts: [],
      externalShifts: [],
      selectedShift: null,
      pendingShiftAssignments: {},
      candidateRequestVersions: {},
      weeklyCandidatesRequestId: 0,
      candidates: { available: [], unavailable: [] },
      loadingCandidates: false,
      drawer: false,

      // Sick Assignment Dialog Data
      sickAssignDialog: false,
      sickAssignEmployee: null,
      sickAssignDetails: null,
      sickAssignCalculationMode: "fixed",
      sickAssignLocation: "start",

      // Override Dialog Data
      overrideReasonDialog: false,
      selectedOverrideEmployee: null,
      overrideReason: null,
      customOverrideReason: "",
      predefinedOverrideReasons: ["Mitarbeiter hat zugestimmt", "Krankheitsvertretung", "Kurzfristiger Personalengpass", "Anderer Grund (Freitext)"],

      unassignedWarningDialog: false,
      weeklyCandidatesCache: {},
      notes: [],
      // Spezialtag Dialog Data
      specialDayDialog: false,
      specialDayDate: null,
      specialDayMode: "replace",
      specialDayShifts: [],
      standardShiftsForDate: [],
      savingSpecialDay: false,
      weeklyTemplateData: [],

      // Daily Notes Dialog
      noteDialog: false,
      activeNoteEmployee: null,
      activeNoteDate: null,
      newNoteText: "",
      savingNote: false,

      // Finish Warning Dialog
      finishWarningDialog: false,
      unassignedCountInMonth: 0,
      deletingUnassigned: false,

      // Phase 2 Additions
      draggedEmployee: null,
      showWeekSummary: true,
      confirmResetWeekDialog: false,
      resettingWeek: false,

      // Manual Add Shift Dialog Data
      addShiftDialog: false,
      addShiftDate: null,
      addShiftTemplateId: null,
      creatingShift: false,

      // Team-Radar (leerer Assistentenbereich)
      teamOverviewFilter: "all",
      teamOverviewSort: "need",
    }
  },
  computed: {
    monthName() {
      return this.startDate ? this.$moment(this.startDate).format("MMMM YYYY") : ""
    },
    assignedEmployeeConflict() {
      if (!this.selectedShift || !this.selectedShift.assignedEmployee) return null
      const assignedId = String(this.selectedShift.assignedEmployee._id || this.selectedShift.assignedEmployee)
      const candidateConflict = Array.isArray(this.candidates?.unavailable)
        ? this.candidates.unavailable.find((employee) => String(employee._id) === assignedId)
        : null
      if (candidateConflict) return candidateConflict

      const employee = this.employees.find((entry) => String(entry.user?._id || entry.user || entry._id) === assignedId)
      if (!employee) return null
      const settings = this.getEffectiveEmployeePlanningSettings(employee, this.selectedShift.date)
      const isApprentice = typeof settings.isApprentice === "boolean" ? settings.isApprentice : employee.isApprentice === true
      const schoolDays = Array.isArray(settings.schoolDays) ? settings.schoolDays : employee.schoolDays || []
      const weekday = this.$moment(this.selectedShift.date).isoWeekday()
      const isSchoolDay = schoolDays.some((schoolDay) => Number(schoolDay?.dayOfWeek ?? schoolDay) === weekday)
      const isSchoolHoliday = (this.schoolHolidays || []).some(
        (holiday) =>
          holiday.startDate && holiday.endDate && this.$moment(this.selectedShift.date).isBetween(holiday.startDate, holiday.endDate, "day", "[]"),
      )
      return isApprentice && isSchoolDay && !isSchoolHoliday ? { ...employee, reasons: ["Berufsschule"] } : null
    },
    weeksInMonth() {
      if (!this.startDate || !this.endDate) return []
      const start = this.$moment(this.startDate).startOf("month")
      const end = this.$moment(this.endDate).endOf("month")

      const weeks = []
      let current = start.clone()

      // Kalenderwochen ermitteln (isoWeek Mo-So), die mit dem Monat schneiden
      while (current.isSameOrBefore(end, "day")) {
        const weekStart = current.clone().startOf("isoWeek")
        const weekEnd = current.clone().endOf("isoWeek")

        // Grenzen auf Monat zuschneiden für Label/Anzeige
        const actualStart = weekStart.isBefore(start) ? start.clone() : weekStart
        const actualEnd = weekEnd.isAfter(end) ? end.clone() : weekEnd

        // Tage sammeln
        const days = []
        let day = actualStart.clone()
        while (day.isSameOrBefore(actualEnd, "day")) {
          days.push({
            dateStr: day.format("YYYY-MM-DD"),
            dayName: day.format("dddd"),
            dayNum: day.format("D"),
            formattedDate: day.format("DD.MM"),
            isWeekend: day.isoWeekday() >= 6,
            isToday: day.isSame(this.$moment(), "day"),
          })
          day.add(1, "day")
        }

        weeks.push({
          start: actualStart.format("YYYY-MM-DD"),
          end: actualEnd.format("YYYY-MM-DD"),
          label: `${actualStart.format("DD.MM")} – ${actualEnd.format("DD.MM")}`,
          days,
        })

        // Nächste Woche anbrechen
        current = weekEnd.clone().add(1, "day")
      }
      return weeks
    },
    currentWeek() {
      return this.weeksInMonth[this.currentWeekIndex] || null
    },
    currentWeekLabel() {
      return this.currentWeek ? this.currentWeek.label : ""
    },
    currentWeekDays() {
      return this.currentWeek ? this.currentWeek.days : []
    },
    unassignedShiftsInCurrentWeek() {
      if (!this.currentWeek) return []
      const wStart = this.currentWeek.start
      const wEnd = this.currentWeek.end
      return this.shifts.filter((s) => !s.assignedEmployee && s.date >= wStart && s.date <= wEnd)
    },
    totalShiftsInCurrentWeek() {
      if (!this.currentWeek) return 0
      const wStart = this.currentWeek.start
      const wEnd = this.currentWeek.end
      return this.shifts.filter((s) => s.date >= wStart && s.date <= wEnd).length
    },
    staffedShiftsInCurrentWeek() {
      if (!this.currentWeek) return 0
      const wStart = this.currentWeek.start
      const wEnd = this.currentWeek.end
      return this.shifts.filter((s) => s.assignedEmployee && s.date >= wStart && s.date <= wEnd).length
    },
    staffingPercentage() {
      const total = this.totalShiftsInCurrentWeek
      if (total === 0) return 0
      return Math.round((this.staffedShiftsInCurrentWeek / total) * 100)
    },
    totalTargetHours() {
      return (this.employees || []).reduce((sum, emp) => {
        return sum + this.calculateSollStunden(emp)
      }, 0)
    },
    totalDaysInPlanningPeriod() {
      return (this.weeksInMonth || []).reduce((sum, w) => sum + (w.days ? w.days.length : 0), 0)
    },
    totalWeeklyTargetHours() {
      const totalDays = this.totalDaysInPlanningPeriod
      if (totalDays <= 0 || !this.currentWeek || !this.currentWeek.days) return 0
      const daysInWeek = this.currentWeek.days.length
      return this.totalTargetHours * (daysInWeek / totalDays)
    },
    totalWeeklyAssignedHours() {
      if (!this.currentWeek) return 0
      const wStart = this.currentWeek.start
      const wEnd = this.currentWeek.end
      const weekShifts = this.shifts.filter((s) => s.assignedEmployee && s.date >= wStart && s.date <= wEnd)
      return weekShifts.reduce((sum, s) => {
        if (!s.times) return sum
        return (
          sum +
          s.times.reduce((tSum, t) => {
            if (t.type === "Arbeitszeit" && t.start && t.end) {
              const start = this.$moment(t.start, "HH:mm")
              const end = this.$moment(t.end, "HH:mm")
              if (end.isBefore(start)) end.add(1, "day")
              return tSum + end.diff(start, "hours", true)
            }
            return tSum
          }, 0)
        )
      }, 0)
    },
    weeklyHoursFulfillmentPercentage() {
      const target = this.totalWeeklyTargetHours
      if (target <= 0) return 0
      return Math.round((this.totalWeeklyAssignedHours / target) * 100)
    },
    employeePlanningOverviewById() {
      if (!this.currentWeek) return {}

      const canonicalEmployeeId = (employeeLike) => {
        const rawId = String(employeeLike?.user?._id || employeeLike?.user || employeeLike?._id || employeeLike || "")
        if (!rawId) return ""
        const employeeRecord = (this.employees || []).find((employee) => {
          const userId = String(employee?.user?._id || employee?.user || "")
          const recordId = String(employee?._id || "")
          return rawId === userId || rawId === recordId
        })
        return String(employeeRecord?.user?._id || employeeRecord?.user || employeeRecord?._id || rawId)
      }
      const employeeIdOf = (employee) => canonicalEmployeeId(employee)
      const shiftEmployeeIdOf = (shift) => canonicalEmployeeId(shift?.assignedEmployee)
      const uniqueShifts = (entries) => {
        const seen = new Set()
        return (entries || []).filter((shift) => {
          const key = String(
            shift?._id ||
              `${shift?.date || ""}_${shiftEmployeeIdOf(shift)}_${shift?.studio?._id || shift?.studio || ""}_${shift?.shiftTemplate?._id || ""}`,
          )
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      }

      const previousShifts = Array.isArray(this.previousContext?.shifts) ? this.previousContext.shifts : []
      const currentExternal = Array.isArray(this.externalShifts) ? this.externalShifts : []
      const monthAllShifts = uniqueShifts([...this.shifts, ...currentExternal]).filter(
        (shift) => shift.date >= this.startDate && shift.date <= this.endDate,
      )
      const contextShifts = uniqueShifts([...previousShifts, ...monthAllShifts])
      const previousWeekStart = this.$moment(this.currentWeek.start).startOf("isoWeek").subtract(1, "week")
      const previousWeekendStart = previousWeekStart.clone().isoWeekday(6)
      const previousWeekendEnd = previousWeekStart.clone().isoWeekday(7)
      const previousWeekendKey = previousWeekendStart.format("GGGG-WW")
      const previousWeekendLabel = `${previousWeekendStart.format("DD.MM.")} – ${previousWeekendEnd.format("DD.MM.")}`
      const openWeekShifts = this.shifts.filter(
        (shift) => !shift.assignedEmployee && shift.date >= this.currentWeek.start && shift.date <= this.currentWeek.end,
      )
      const overview = {}

      ;(this.employees || []).forEach((employee) => {
        const id = employeeIdOf(employee)
        if (!id) return

        const user = employee.user && typeof employee.user === "object" ? employee.user : null
        const name =
          employee.fullName ||
          user?.fullName ||
          `${user?.vorname || employee.vorname || ""} ${user?.nachname || employee.nachname || ""}`.trim() ||
          "Mitarbeiter"
        const employeeMonthShifts = monthAllShifts.filter((shift) => shiftEmployeeIdOf(shift) === id)
        const localMonthShifts = this.shifts.filter((shift) => shiftEmployeeIdOf(shift) === id)
        const employeeWeekShifts = employeeMonthShifts.filter((shift) => shift.date >= this.currentWeek.start && shift.date <= this.currentWeek.end)
        const previousWeekendShifts = contextShifts.filter(
          (shift) =>
            shiftEmployeeIdOf(shift) === id &&
            shift.date >= previousWeekendStart.format("YYYY-MM-DD") &&
            shift.date <= previousWeekendEnd.format("YYYY-MM-DD"),
        )

        const historicalWeekendWeeks = new Set(this.previousContext?.weekendStats?.[id]?.weeks || [])
        employeeMonthShifts.forEach((shift) => {
          if (this.$moment(shift.date).isoWeekday() >= 6) historicalWeekendWeeks.add(this.$moment(shift.date).format("GGGG-WW"))
        })

        // Der Radar zeigt die verbleibende Kapazität dieses Studios. Das
        // globale Vertragssoll bleibt unverändert; echte Fremdstudio-Stunden
        // werden nur von der lokalen Zielprojektion abgezogen.
        const plannedHours = localMonthShifts.reduce((sum, shift) => sum + this.getShiftHours(shift), 0)
        const studioHours = this.getStudioHoursProjection(employee, plannedHours)
        const targetHours = studioHours.targetHours
        const balanceHours = plannedHours - targetHours
        const progress = targetHours > 0 ? Math.round((plannedHours / targetHours) * 100) : 0
        let hoursState = "balanced"
        let hoursStateLabel = "Im Soll"
        if (targetHours <= 0) {
          hoursState = "neutral"
          hoursStateLabel = "Kein Studio-Soll"
        } else if (balanceHours < -0.25) {
          hoursState = "under"
          hoursStateLabel = `${Math.abs(balanceHours).toFixed(1)}h offen`
        } else if (balanceHours > 0.25) {
          hoursState = "over"
          hoursStateLabel = `+${balanceHours.toFixed(1)}h über Soll`
        }

        const stats = { morning: 0, intermediate: 0, late: 0 }
        employeeMonthShifts.forEach((shift) => {
          this.determineShiftCategories(shift).forEach((category) => {
            if (Object.prototype.hasOwnProperty.call(stats, category)) stats[category]++
          })
        })

        let availableOpenCount = 0
        let blockedOpenCount = 0
        let evaluatedOpenCount = 0
        const conflictReasons = new Set()
        openWeekShifts.forEach((shift) => {
          const cached = this.weeklyCandidatesCache[shift._id]
          if (!cached) return
          evaluatedOpenCount++
          if ((cached.available || []).some((candidate) => String(candidate._id) === id)) {
            availableOpenCount++
          }
          const unavailable = (cached.unavailable || []).find((candidate) => String(candidate._id) === id)
          if (unavailable) {
            blockedOpenCount++
            ;(unavailable.reasons || []).forEach((reason) => conflictReasons.add(this.getConflictGerman(reason)))
          }
        })

        const days = this.currentWeekDays.map((day) => {
          const shifts = employeeWeekShifts.filter((shift) => shift.date === day.dateStr)
          const localShifts = shifts.filter((shift) => String(shift.studio?._id || shift.studio || this.studioId) === String(this.studioId))
          const externalShifts = shifts.filter((shift) => !localShifts.includes(shift))
          const statuses = this.getEmployeeStatus(employee, day.dateStr)
          let state = "free"
          let stateLabel = "Frei planbar"
          if (localShifts.length) {
            state = "assigned"
            stateLabel = localShifts.map((shift) => shift.shiftTemplate?.name || "Schicht").join(", ")
          } else if (externalShifts.length) {
            state = "external"
            stateLabel = "Einsatz in anderem Studio"
          } else if (statuses.length) {
            const normalizedStatuses = statuses.map((status) => String(status).toLowerCase())
            state = normalizedStatuses.some((status) => status.includes("ferien"))
              ? "vacation"
              : normalizedStatuses.some((status) => status.includes("schule"))
              ? "school"
              : normalizedStatuses.some((status) => status.includes("freiwunsch"))
              ? "wish"
              : normalizedStatuses.some((status) => ["krank", "militär", "abwesen", "schwanger"].some((term) => status.includes(term)))
              ? "absence"
              : "blocked"
            stateLabel = statuses.join(", ")
          }
          return {
            date: day.dateStr,
            label: this.$moment(day.dateStr).format("dddd, DD.MM."),
            shortLabel: this.$moment(day.dateStr).format("dd").slice(0, 2),
            state,
            stateLabel,
            shiftCount: shifts.length,
            hours: shifts.reduce((sum, shift) => sum + this.getShiftHours(shift), 0),
            statuses,
          }
        })

        overview[id] = {
          id,
          employee,
          name,
          percentage: this.getEmployeePercentage(employee),
          targetHours,
          plannedHours,
          globalTargetHours: studioHours.globalTargetHours,
          externalPlannedHours: studioHours.externalPlannedHours,
          balanceHours,
          progress,
          hoursState,
          hoursStateLabel,
          weekShiftCount: employeeWeekShifts.length,
          weekHours: employeeWeekShifts.reduce((sum, shift) => sum + this.getShiftHours(shift), 0),
          previousWeekendShiftCount: previousWeekendShifts.length,
          previousWeekendWorked: previousWeekendShifts.length > 0 || historicalWeekendWeeks.has(previousWeekendKey),
          previousWeekendLabel,
          monthWeekendShiftCount: employeeMonthShifts.filter((shift) => this.$moment(shift.date).isoWeekday() >= 6).length,
          weekendWeeksCount: historicalWeekendWeeks.size,
          stats,
          days,
          availableOpenCount,
          blockedOpenCount,
          evaluatedOpenCount,
          conflictReasons: [...conflictReasons].slice(0, 3),
        }
      })

      return overview
    },
    employeePlanningOverview() {
      let rows = Object.values(this.employeePlanningOverviewById)
      if (this.teamOverviewFilter === "capacity") {
        rows = rows.filter((row) => row.targetHours > 0 && row.balanceHours < -0.25)
      } else if (this.teamOverviewFilter === "conflicts") {
        rows = rows.filter((row) => row.blockedOpenCount > 0 || row.days.some((day) => !["free", "assigned", "external"].includes(day.state)))
      }

      return rows.sort((a, b) => {
        if (this.teamOverviewSort === "name") return a.name.localeCompare(b.name, "de")
        if (a.targetHours > 0 !== b.targetHours > 0) return a.targetHours > 0 ? -1 : 1
        if (a.balanceHours !== b.balanceHours) return a.balanceHours - b.balanceHours
        if (a.availableOpenCount !== b.availableOpenCount) return b.availableOpenCount - a.availableOpenCount
        return a.name.localeCompare(b.name, "de")
      })
    },
    teamOverviewStats() {
      const rows = Object.values(this.employeePlanningOverviewById)
      return {
        total: rows.length,
        underTarget: rows.filter((row) => row.targetHours > 0 && row.balanceHours < -0.25).length,
        openHours: rows.reduce((sum, row) => sum + (row.balanceHours < 0 ? Math.abs(row.balanceHours) : 0), 0),
      }
    },
    isOverrideFormValid() {
      if (!this.overrideReason) return false
      if (this.overrideReason === "Anderer Grund (Freitext)") {
        return !!this.customOverrideReason.trim()
      }
      return true
    },
    suggestedFixedShiftsCount() {
      if (!this.currentWeek || this.shifts.length === 0) return 0

      const unassigned = this.shifts.filter((s) => !s.assignedEmployee && s.date >= this.currentWeek.start && s.date <= this.currentWeek.end)

      let count = 0

      unassigned.forEach((shift) => {
        if (!shift.shiftTemplate) return

        const weekday = this.$moment(shift.date).isoWeekday()
        const templateId = String(shift.shiftTemplate._id || shift.shiftTemplate)

        const cache = this.weeklyCandidatesCache[shift._id]
        if (!cache || !cache.available) return

        const hasMatching = cache.available.some((candidate) => {
          const empRecord = this.employees.find((e) => String(e.user?._id || e.user) === String(candidate._id))
          if (!empRecord) return false

          const savedSettings = this.getEffectiveEmployeePlanningSettings(empRecord, shift.date)
          const fixedShifts = savedSettings.fixedShifts || empRecord.planningSettings?.fixedShifts || {}

          return String(fixedShifts[weekday]) === templateId
        })

        if (hasMatching) count++
      })

      return count
    },
  },
  async created() {
    await this.fetchShifts(false, Boolean(this.initialShiftId))
    if (this.initialShiftId) await this.focusInitialShift(this.initialShiftId)
    await this.fetchWeeklyTemplate()
  },
  watch: {
    specialDayMode(newMode) {
      if (newMode === "add") {
        this.specialDayShifts = this.specialDayShifts.filter((s) => !s.isStandard)
      } else if (newMode === "replace") {
        if (this.specialDayShifts.length === 0) {
          this.specialDayShifts = JSON.parse(JSON.stringify(this.standardShiftsForDate))
        }
      }
    },
  },
  methods: {
    async focusInitialShift(shiftId) {
      const shift = this.shifts.find((entry) => String(entry._id) === String(shiftId))
      if (!shift) return
      const weekIndex = this.weeksInMonth.findIndex((week) => shift.date >= week.start && shift.date <= week.end)
      if (weekIndex >= 0) this.currentWeekIndex = weekIndex
      await this.loadWeeklyCandidatesCache()
      await this.selectShift(shift)
    },
    getEffectiveEmployeePlanningSettings(employee, targetDate = this.startDate) {
      const studioSettings = (employee?.shiftPlanningSettings && employee.shiftPlanningSettings[this.studioId]) || {}
      const { monthlyOverrides, ...baseSettings } = studioSettings
      const monthKey = this.$moment(targetDate || this.startDate).format("YYYY-MM")
      const monthlySettings = (monthlyOverrides && monthlyOverrides[monthKey]) || {}
      const backendSettings = {
        ...baseSettings,
        ...monthlySettings,
        percentages: {
          ...(monthlySettings.percentages || {}),
        },
        fixedShifts: {
          ...(baseSettings.fixedShifts || {}),
          ...(monthlySettings.fixedShifts || {}),
        },
      }

      let fallbackSettings = backendSettings
      if (Object.keys(baseSettings).length === 0 && Object.keys(monthlySettings).length === 0) {
        try {
          const storageKey = `planningSettings_${employee?._id}_${this.studioId}`
          const localSettings = JSON.parse(localStorage.getItem(storageKey)) || {}
          fallbackSettings = {
            ...localSettings,
            // Der alte Schlüssel enthält keinen Monat und ist keine zulässige
            // Prozentquelle für die Schichtplanung.
            percentages: {},
          }
        } catch (error) {
          fallbackSettings = {}
        }
      }

      const currentSettings = employee?.planningSettings || {}
      return {
        ...fallbackSettings,
        ...currentSettings,
        percentages: {
          ...(fallbackSettings.percentages || {}),
          ...(currentSettings.percentages || {}),
        },
        fixedShifts: {
          ...(fallbackSettings.fixedShifts || {}),
          ...(currentSettings.fixedShifts || {}),
        },
      }
    },
    getExternalEmployeesForDay(dateStr) {
      const dayMoment = this.$moment(dateStr)
      const dayOfWeekISO = dayMoment.isoWeekday()
      const list = []

      this.employees.forEach((emp) => {
        const empId = String(emp.user?._id || emp.user)

        // 1. Check if they have an active shift in another studio on this date
        const extShift =
          this.externalShifts.find((s) => {
            const sEmpId = String(s.assignedEmployee?._id || s.assignedEmployee)
            return sEmpId === empId && s.date === dateStr
          }) ||
          this.shifts.find((s) => {
            const sEmpId = String(s.assignedEmployee?._id || s.assignedEmployee)
            return s.isVirtualExternal && sEmpId === empId && s.date === dateStr
          })

        if (extShift) {
          if (extShift.isVirtualExternal) {
            const studioName = extShift.studio?.name || "Anderes Center"
            list.push({
              employee: emp,
              statusText: `${studioName}: Einsatz bestätigt (Zeiten unbestätigt)`,
              hasShift: true,
              isVirtual: true,
              shiftId: extShift._id,
            })
          } else {
            const times = extShift.times && extShift.times.length > 0 ? extShift.times : extShift.shiftTemplate ? extShift.shiftTemplate.times : []
            const sorted = [...times].filter((t) => t.type !== "Pause").sort((a, b) => a.start.localeCompare(b.start))
            const timeStr = sorted.length > 0 ? ` (${sorted[0].start} - ${sorted[sorted.length - 1].end})` : ""
            const shiftName = extShift.shiftTemplate ? extShift.shiftTemplate.name : "Schicht"
            const studioName = extShift.studio ? extShift.studio.name : "Anderes Center"

            list.push({
              employee: emp,
              statusText: `${studioName}: ${shiftName}${timeStr}`,
              hasShift: true,
              isVirtual: false,
            })
          }
        } else {
          const recurringExternal = emp.planningSettings?.recurringExternalStudio || []
          if (recurringExternal.includes(dayOfWeekISO)) {
            list.push({
              employee: emp,
              statusText: "Heute in einem anderen Center (noch keine Schicht)",
              hasShift: false,
              isVirtual: false,
            })
          }
        }
      })

      return list
    },
    async fetchShifts(background = false, skipCandidateCache = false) {
      if (!background) this.loading = true
      try {
        const [response, notesRes] = await Promise.all([
          api.shiftManagement.fetchPlan(this.studioId, this.startDate, this.endDate, true),
          api.shiftManagement.fetchNotes(this.studioId, this.startDate, this.endDate),
        ])

        const responseData = response && response.data ? response.data : response
        this.shifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []
        this.externalShifts = responseData && responseData.externalShifts ? responseData.externalShifts : []
        this.notes = notesRes || []

        if (!skipCandidateCache) await this.loadWeeklyCandidatesCache()
      } catch (e) {
        console.error("Fetch Error:", e)
        this.$toast.error("Fehler beim Laden der Schichten.")
      } finally {
        if (!background) this.loading = false
      }
    },
    getEmployeePicture(assignedEmployee) {
      if (!assignedEmployee) return "/img/default-avatar.png"
      if (assignedEmployee.picture) {
        return this.normalizeEmployeePicture(assignedEmployee.picture)
      }
      const empId = String(assignedEmployee._id || assignedEmployee)
      const empRecord = this.employees.find((e) => {
        const uId = String(e.user?._id || e.user)
        const eId = String(e._id)
        return uId === empId || eId === empId
      })
      if (empRecord && empRecord.picture) {
        return this.normalizeEmployeePicture(empRecord.picture)
      }
      if (assignedEmployee.user && assignedEmployee.user.picture) {
        return this.normalizeEmployeePicture(assignedEmployee.user.picture)
      }
      return "/img/default-avatar.png"
    },
    normalizeEmployeePicture(picture) {
      if (!picture) return "/img/default-avatar.png"
      const value = String(picture).trim()
      if (/^(data:|blob:|https?:\/\/)/i.test(value) || value.startsWith("/img/")) return value
      return `/img/profilbilder/${value.replace(/^\/+/, "")}`
    },
    useDefaultEmployeePicture(event) {
      const image = event && event.target
      if (!image || String(image.src || "").includes("/img/default-avatar.png")) return
      image.src = "/img/default-avatar.png"
    },
    getShiftHours(shift) {
      if (!shift) return 0
      const times = shift.times && shift.times.length ? shift.times : shift.shiftTemplate?.times || []
      return times.reduce((sum, time) => {
        if (time.type !== "Arbeitszeit" || !time.start || !time.end) return sum
        const start = this.$moment(time.start, "HH:mm")
        const end = this.$moment(time.end, "HH:mm")
        if (end.isBefore(start)) end.add(1, "day")
        return sum + end.diff(start, "hours", true)
      }, 0)
    },
    getEmployeeUserId(employeeLike) {
      if (!employeeLike) return ""
      return String(employeeLike.user?._id || employeeLike.user || employeeLike._id || employeeLike)
    },
    getEmployeeRecord(employeeLike) {
      const employeeIds = new Set(
        [employeeLike?.user?._id, employeeLike?.user, employeeLike?._id, employeeLike]
          .filter((value) => value !== undefined && value !== null && typeof value !== "object")
          .map(String),
      )
      if (!employeeIds.size) return null
      return (
        (this.employees || []).find((employee) => {
          const userId = String(employee?.user?._id || employee?.user || "")
          const recordId = String(employee?._id || "")
          return employeeIds.has(userId) || employeeIds.has(recordId)
        }) || null
      )
    },
    getLocalPlannedHours(employeeLike) {
      const employeeId = this.getEmployeeUserId(employeeLike)
      if (!employeeId) return 0

      const seenShiftIds = new Set()
      return (this.shifts || []).reduce((sum, shift) => {
        const assignedEmployeeId = String(shift?.assignedEmployee?._id || shift?.assignedEmployee || "")
        if (assignedEmployeeId !== employeeId || shift.date < this.startDate || shift.date > this.endDate) return sum

        const shiftStudioId = String(shift?.studio?._id || shift?.studio || this.studioId)
        if (shiftStudioId !== String(this.studioId)) return sum

        const shiftId = shift?._id ? String(shift._id) : ""
        if (shiftId && seenShiftIds.has(shiftId)) return sum
        if (shiftId) seenShiftIds.add(shiftId)
        return sum + this.getShiftHours(shift)
      }, 0)
    },
    getFallbackExternalPlannedHours(employeeLike) {
      const employeeId = this.getEmployeeUserId(employeeLike)
      if (!employeeId) return 0

      const seenShiftIds = new Set()
      return (this.externalShifts || []).reduce((sum, shift) => {
        const assignedEmployeeId = String(shift?.assignedEmployee?._id || shift?.assignedEmployee || "")
        if (assignedEmployeeId !== employeeId || shift.date < this.startDate || shift.date > this.endDate) return sum
        if (shift.status === "unassigned" || shift.deleted === true || shift.isDeleted === true) return sum

        const shiftStudioId = String(shift?.studio?._id || shift?.studio || "")
        if (shiftStudioId && shiftStudioId === String(this.studioId)) return sum

        const shiftId = shift?._id ? String(shift._id) : ""
        if (shiftId && seenShiftIds.has(shiftId)) return sum
        if (shiftId) seenShiftIds.add(shiftId)

        // Virtuelle Fremdeinsätze besitzen keine Zeiten und tragen deshalb 0h bei.
        return sum + this.getShiftHours(shift)
      }, 0)
    },
    getStudioHoursProjection(employeeLike, suppliedLocalPlannedHours) {
      const employeeRecord = this.getEmployeeRecord(employeeLike)
      const summary = employeeLike?.hoursSummary || employeeRecord?.hoursSummary
      const summaryTarget = Number(summary?.total?.netTargetHours)
      const employeeTarget = Number(employeeRecord?.sollStunden ?? employeeLike?.sollStunden)
      const globalTargetHours = Number.isFinite(summaryTarget) ? summaryTarget : Number.isFinite(employeeTarget) ? employeeTarget : 0

      const totalPlannedHours = Number(summary?.total?.plannedHours)
      const localSummaryHours = Number(summary?.byStudio?.[String(this.studioId)]?.plannedHours || 0)
      const hasCentralPlanBreakdown = Number.isFinite(totalPlannedHours)
      const externalPlannedHours = hasCentralPlanBreakdown
        ? Math.max(0, totalPlannedHours - localSummaryHours)
        : this.getFallbackExternalPlannedHours(employeeLike)
      const studioTargetHours = Math.max(0, globalTargetHours - externalPlannedHours)
      const parsedLocalPlannedHours = Number(suppliedLocalPlannedHours)
      const studioPlannedHours = Number.isFinite(parsedLocalPlannedHours) ? parsedLocalPlannedHours : this.getLocalPlannedHours(employeeLike)

      return {
        globalTargetHours,
        externalPlannedHours,
        targetHours: studioTargetHours,
        plannedHours: studioPlannedHours,
        balanceHours: studioPlannedHours - studioTargetHours,
        source: hasCentralPlanBreakdown ? "summary" : "externalShifts",
      }
    },
    getCandidateStudioTargetHours(candidate) {
      return this.getStudioHoursProjection(candidate).targetHours
    },
    getCandidateStudioPlannedHours(candidate) {
      return this.getStudioHoursProjection(candidate).plannedHours
    },
    getEmployeePlanningOverview(employeeLike) {
      if (!employeeLike) return null
      const employeeRecord = this.getEmployeeRecord(employeeLike)
      const possibleIds = [
        employeeRecord?.user?._id,
        employeeRecord?.user,
        employeeRecord?._id,
        employeeLike?.user?._id,
        employeeLike?.user,
        employeeLike?._id,
        employeeLike,
      ]
        .filter((value) => value !== undefined && value !== null && typeof value !== "object")
        .map(String)
      const matchingId = possibleIds.find((id) => this.employeePlanningOverviewById[id])
      return matchingId ? this.employeePlanningOverviewById[matchingId] : null
    },
    formatEmployeeShortName(fullName) {
      if (!fullName) return ""
      const parts = fullName.trim().split(/\s+/)
      if (parts.length < 2) return fullName
      const firstName = parts[0]
      const lastName = parts[parts.length - 1]
      return `${firstName} ${lastName.charAt(0)}.`
    },
    isAutomatic(shift) {
      if (!shift || !shift.allocationNote) return false
      const note = shift.allocationNote.toLowerCase()
      return note.includes("auto") || note.includes("fix")
    },
    getConsecutiveStreak(candidate, dateStr) {
      if (candidate && typeof candidate.streak === "number") {
        return candidate.streak
      }
      return 0
    },
    getStreakColor(streak) {
      if (streak >= 5) return "red--text text--lighten-1 font-weight-bold"
      if (streak === 4) return "orange--text text--lighten-1 font-weight-bold"
      if (streak === 1) return "green--text text--lighten-2 font-weight-semibold"
      return "grey--text text--lighten-2"
    },
    getStreakDisplayText(candidate, dateStr) {
      if (candidate && typeof candidate.streak === "number" && candidate.streak > 0) {
        return `Serie: ${candidate.streak + 1} Tage`
      }
      return ""
    },
    getLastWorkDisplayText(candidate, dateStr) {
      if (candidate && candidate.lastWorkText) {
        if (candidate.lastWorkText === "gestern") return "Letzter Arbeitstag: gestern"
        return `Letzter Arbeitstag ${candidate.lastWorkText}`
      }
      return "Noch kein Arbeitstag"
    },
    getRecommendationReasons(candidate) {
      if (!candidate) return []
      const reasons = []

      if (candidate.hasPreferredPartner) {
        reasons.push("Bevorzugter Teampartner ist am selben Tag eingeteilt.")
      }

      // Blockwise positive recommendations
      if (this.planningStrategy === "blockwise" && this.selectedShift) {
        const times =
          this.selectedShift.times && this.selectedShift.times.length > 0
            ? this.selectedShift.times
            : this.selectedShift.shiftTemplate
            ? this.selectedShift.shiftTemplate.times
            : []
        const currentShiftType = this.determineShiftType(this.selectedShift, times)

        const typeStreak = this.getShiftTypeStreak(candidate, this.selectedShift.date, currentShiftType)
        const workedThisType = candidate.typeCounts ? candidate.typeCounts[currentShiftType] || 0 : 0

        if (typeStreak > 0) {
          const typeLabel =
            { morning: "Frühschichten", intermediate: "Zwischenschichten", late: "Spätschichten" }[currentShiftType] || "gleichen Schichten"
          reasons.push(`Führt den Block an ${typeLabel} fort.`)
        } else if (workedThisType > 0) {
          const typeLabel =
            { morning: "Frühschichten", intermediate: "Zwischenschichten", late: "Spätschichten" }[currentShiftType] || "gleichen Schichten"
          reasons.push(`Bevorzugter Schichttyp (${typeLabel}) in dieser Periode.`)
        }
      }

      // 1. Fixed shift target matching
      if (this.selectedShift && this.selectedShift.shiftTemplate) {
        const weekday = this.$moment(this.selectedShift.date).isoWeekday()
        const templateId = String(this.selectedShift.shiftTemplate._id || this.selectedShift.shiftTemplate)

        // Find employee object to check fixedShifts
        const empRecord = this.employees.find((e) => String(e.user?._id || e.user) === String(candidate._id))
        if (empRecord) {
          const savedSettings = this.getEffectiveEmployeePlanningSettings(empRecord, this.selectedShift.date)
          const fixedShifts = savedSettings.fixedShifts || empRecord.planningSettings?.fixedShifts || {}

          if (String(fixedShifts[weekday]) === templateId) {
            reasons.push("Stammschicht für diesen Wochentag.")
          }
        }
      }

      // 1b. Worked yesterday check & streak calculator
      if (this.selectedShift && this.selectedShift.date) {
        const prevDateStr = this.$moment(this.selectedShift.date).subtract(1, "day").format("YYYY-MM-DD")
        const empId = String(candidate._id)
        const workedYesterday = this.shifts.some(
          (s) => s.date === prevDateStr && s.assignedEmployee && String(s.assignedEmployee._id || s.assignedEmployee) === empId,
        )
        if (workedYesterday) {
          let streak = 1
          let checkDate = this.$moment(prevDateStr).subtract(1, "day")
          for (let i = 0; i < 7; i++) {
            const checkStr = checkDate.format("YYYY-MM-DD")
            const worked = this.shifts.some(
              (s) => s.date === checkStr && s.assignedEmployee && String(s.assignedEmployee._id || s.assignedEmployee) === empId,
            )
            if (worked) {
              streak++
              checkDate.subtract(1, "day")
            } else {
              break
            }
          }
          reasons.push(`Anhängende Schicht (Serie von ${streak + 1} Arbeitstagen).`)
        }
      }

      // 2. Wish fulfilled
      if (candidate.hasMatchingWish) {
        reasons.push("Erfüllt einen eingetragenen Schichtwunsch.")
      }

      // 3. Weekly pacing availability
      if (candidate.shiftsThisWeek < candidate.weeklyShiftTarget) {
        reasons.push(`Offene Kapazität diese Woche (${candidate.shiftsThisWeek}/${candidate.weeklyShiftTarget} Schichten).`)
      }

      // 4. Monthly hours target
      const studioHours = this.getStudioHoursProjection(candidate)
      if (studioHours.plannedHours < studioHours.targetHours) {
        reasons.push(`Soll-Stunden noch offen (${studioHours.plannedHours.toFixed(0)}h / ${studioHours.targetHours.toFixed(0)}h).`)
      }

      // 5. Rotation / Fair distribution
      if (reasons.length === 0) {
        reasons.push("Bietet die fairste Rotation im Team.")
      }

      return reasons.slice(0, 2) // Return up to top 2 reasons for clean spacing
    },
    hasWeekendConflict(emp, dateStr) {
      const empRecord = this.employees.find((e) => String(e.user?._id || e.user) === String(emp._id))
      if (!empRecord) return false
      const empId = String(emp._id)

      const selectedMoment = this.$moment(dateStr)
      const isWeekend = selectedMoment.isoWeekday() >= 6
      if (!isWeekend) return false

      const prevWeekendStart = selectedMoment.clone().subtract(1, "week").startOf("isoWeek").add(5, "days")
      const prevWeekendEnd = prevWeekendStart.clone().add(1, "day")
      const workedPrevWeekend = this.shifts.some(
        (s) =>
          s.assignedEmployee &&
          String(s.assignedEmployee._id || s.assignedEmployee) === empId &&
          (s.date === prevWeekendStart.format("YYYY-MM-DD") || s.date === prevWeekendEnd.format("YYYY-MM-DD")),
      )

      const nextWeekendStart = selectedMoment.clone().add(1, "week").startOf("isoWeek").add(5, "days")
      const nextWeekendEnd = nextWeekendStart.clone().add(1, "day")
      const worksNextWeekend = this.shifts.some(
        (s) =>
          s.assignedEmployee &&
          String(s.assignedEmployee._id || s.assignedEmployee) === empId &&
          (s.date === nextWeekendStart.format("YYYY-MM-DD") || s.date === nextWeekendEnd.format("YYYY-MM-DD")),
      )

      return workedPrevWeekend || worksNextWeekend
    },
    getZeroSuitabilityReasons(emp) {
      const reasons = []
      const shiftsThisWeek = parseInt(emp.shiftsThisWeek, 10) || 0
      const weeklyShiftTarget = parseInt(emp.weeklyShiftTarget, 10) || 0
      const studioHours = this.getStudioHoursProjection(emp)
      const plannedHours = studioHours.plannedHours
      const sollStunden = studioHours.targetHours

      if (shiftsThisWeek >= weeklyShiftTarget) {
        reasons.push("Wochenziel an Schichten bereits erreicht.")
      }
      if (plannedHours >= sollStunden) {
        reasons.push("Monats-Sollstunden bereits erreicht.")
      }
      if (this.selectedShift && this.hasWeekendConflict(emp, this.selectedShift.date)) {
        reasons.push("Würde an aufeinanderfolgenden Wochenenden arbeiten.")
      }
      if (emp.unverifiedRestPeriod) {
        reasons.push("Ruhezeit-Prüfung unvollständig (Zeiten der Fremdschicht unbekannt).")
      }

      // Detaillierte Gründe ermitteln
      if (this.selectedShift) {
        const times =
          this.selectedShift.times && this.selectedShift.times.length > 0
            ? this.selectedShift.times
            : this.selectedShift.shiftTemplate
            ? this.selectedShift.shiftTemplate.times
            : []
        const currentShiftType = this.determineShiftType(this.selectedShift, times)

        // Monotonie-Prävention (nur im Rotationsmodus aktiv)
        if (this.planningStrategy !== "blockwise") {
          const typeStreak = this.getShiftTypeStreak(emp, this.selectedShift.date, currentShiftType)
          if (typeStreak >= 3) {
            const typeLabel =
              { morning: "Frühschichten", intermediate: "Zwischenschichten", late: "Spätschichten" }[currentShiftType] || "gleichen Schichten"
            reasons.push(`Hat bereits ${typeStreak} ${typeLabel} in Folge gearbeitet (Monotonie-Bremse).`)
          }
        }

        // Blockwise negative warning
        if (this.planningStrategy === "blockwise") {
          const otherTypes = ["morning", "intermediate", "late"].filter((t) => t !== currentShiftType)
          let activeOtherType = null
          let maxOtherStreak = 0

          otherTypes.forEach((t) => {
            const streak = this.getShiftTypeStreak(emp, this.selectedShift.date, t)
            if (streak > maxOtherStreak) {
              maxOtherStreak = streak
              activeOtherType = t
            }
          })

          if (maxOtherStreak > 0) {
            const currentLabel = { morning: "Frühdienst", intermediate: "Zwischendienst", late: "Spätdienst" }[currentShiftType] || "Schicht"
            const finalPluralLabel =
              activeOtherType === "morning" ? "Frühschichten" : activeOtherType === "late" ? "Spätschichten" : "Zwischenschichten"
            reasons.push(`Schicht (${currentLabel}) würde den laufenden Block an ${finalPluralLabel} brechen.`)
          }
        }

        // Spät-zu-Früh Schichtwechsel / Genaue Ruhezeitberechnung
        const yesterdayStr = this.$moment(this.selectedShift.date).subtract(1, "days").format("YYYY-MM-DD")
        let yesterdayShiftType = null
        let lastEnd = null
        this.shifts.forEach((s) => {
          if (s.assignedEmployee && String(s.assignedEmployee._id || s.assignedEmployee) === String(emp._id)) {
            const sTimes = s.times && s.times.length > 0 ? s.times : s.shiftTemplate ? s.shiftTemplate.times : []
            if (sTimes.length > 0) {
              const sEndMoment = this.$moment(`${s.date} ${sTimes[sTimes.length - 1].end}`, "YYYY-MM-DD HH:mm")
              if (sEndMoment.isBefore(this.$moment(`${s.date} ${sTimes[0].start}`, "YYYY-MM-DD HH:mm"))) {
                sEndMoment.add(1, "day")
              }
              if (times.length > 0) {
                const currentStartMoment = this.$moment(`${this.selectedShift.date} ${times[0].start}`, "YYYY-MM-DD HH:mm")
                if (sEndMoment.isBefore(currentStartMoment)) {
                  if (!lastEnd || sEndMoment.isAfter(lastEnd)) {
                    lastEnd = sEndMoment
                    yesterdayShiftType = this.determineShiftType(s, sTimes)
                  }
                }
              } else {
                if (s.date === yesterdayStr) {
                  yesterdayShiftType = this.determineShiftType(s, sTimes)
                }
              }
            }
          }
        })

        if (lastEnd && times.length > 0) {
          const currentStartMoment = this.$moment(`${this.selectedShift.date} ${times[0].start}`, "YYYY-MM-DD HH:mm")
          const diffHours = currentStartMoment.diff(lastEnd, "hours", true)
          if (diffHours < 11) {
            reasons.push(`Ungenügende Ruhezeit (Ruhezeit beträgt nur ${Math.round(diffHours * 10) / 10} Stunden).`)
          }
        } else if (yesterdayShiftType === "late" && currentShiftType === "morning") {
          reasons.push("Ungenügende Ruhezeit (Spät-zu-Früh Schichtwechsel).")
        }
      }

      if (reasons.length === 0) {
        if (this.planningStrategy === "blockwise") {
          reasons.push("Anderer Schichttyp blockiert (andere Kollegen haben Vorrang).")
        } else {
          reasons.push("Algorithmus bevorzugt aktuell andere Kollegen (Faire Verteilung & Rotation).")
        }
      }
      return reasons
    },
    hasActiveWarnings(emp) {
      return this.getActiveWarnings(emp).length > 0
    },
    getActiveWarnings(emp) {
      const reasons = this.getZeroSuitabilityReasons(emp)
      return reasons.filter((r) => r.includes("brechen") || r.includes("Ruhezeit") || r.includes("Monotonie") || r.includes("Wochenenden"))
    },
    getActiveWarningsText(emp) {
      if (this.getScorePercentage(emp.score) === 0) {
        return this.getZeroSuitabilityReasons(emp).join(" / ")
      }
      return this.getActiveWarnings(emp).join(" / ")
    },
    determineShiftType(shift, times) {
      const cats = this.determineShiftCategories(shift)
      return cats && cats.length > 0 ? cats[0] : "intermediate"
    },
    getCompactName(fullName) {
      if (!fullName) return ""
      const parts = fullName.trim().split(/\s+/)
      if (parts.length === 1) return parts[0]
      if (parts.length >= 2) {
        const first = parts[0]
        const last = parts[parts.length - 1]
        return `${first} ${last[0]}.`
      }
      return fullName
    },
    getOpeningShiftStatus(dateStr) {
      const dayShifts = this.getShiftsForDay(dateStr)
      if (dayShifts.length === 0) {
        return { color: "grey darken-3", textClass: "grey--text", icon: "mdi-door-closed", label: "Geschlossen", tooltip: "Keine Schichten geplant" }
      }

      const openingShifts = dayShifts.filter((s) => s.shiftTemplate && s.shiftTemplate.isOpeningShift)
      if (openingShifts.length === 0) {
        return {
          color: "red darken-4",
          textClass: "red--text text--lighten-4",
          icon: "mdi-alert-circle-outline",
          label: "Fehlt",
          tooltip: "An diesem Tag ist keine Eröffnungsschicht geplant!",
        }
      }

      const assigned = openingShifts.filter((s) => s.assignedEmployee)
      if (assigned.length > 0) {
        const names = assigned.map((s) => this.resolveEmployee(s.assignedEmployee)?.fullName || "Unbekannt")
        const compact = names.map((n) => this.getCompactName(n)).join(", ")
        const full = names.join(", ")
        return {
          color: "green darken-3",
          textClass: "green--text text--lighten-4",
          icon: "mdi-key-variant",
          label: compact,
          tooltip: `Eröffnung besetzt durch: ${full}`,
        }
      } else {
        return {
          color: "orange darken-4",
          textClass: "orange--text text--lighten-4",
          icon: "mdi-key-remove",
          label: "Offen",
          tooltip: "Die Eröffnungsschicht ist noch keinem Mitarbeiter zugewiesen!",
        }
      }
    },
    getClosingShiftStatus(dateStr) {
      const dayShifts = this.getShiftsForDay(dateStr)
      if (dayShifts.length === 0) {
        return { color: "grey darken-3", textClass: "grey--text", icon: "mdi-door-closed", label: "Geschlossen", tooltip: "Keine Schichten geplant" }
      }

      const closingShifts = dayShifts.filter((s) => s.shiftTemplate && s.shiftTemplate.isClosingShift)
      if (closingShifts.length === 0) {
        return {
          color: "red darken-4",
          textClass: "red--text text--lighten-4",
          icon: "mdi-alert-circle-outline",
          label: "Fehlt",
          tooltip: "An diesem Tag ist keine Schliessungsschicht geplant!",
        }
      }

      const assigned = closingShifts.filter((s) => s.assignedEmployee)
      if (assigned.length > 0) {
        const names = assigned.map((s) => this.resolveEmployee(s.assignedEmployee)?.fullName || "Unbekannt")
        const compact = names.map((n) => this.getCompactName(n)).join(", ")
        const full = names.join(", ")
        return {
          color: "green darken-3",
          textClass: "green--text text--lighten-4",
          icon: "mdi-lock-outline",
          label: compact,
          tooltip: `Schliessung besetzt durch: ${full}`,
        }
      } else {
        return {
          color: "orange darken-4",
          textClass: "orange--text text--lighten-4",
          icon: "mdi-lock-open-variant-outline",
          label: "Offen",
          tooltip: "Die Schliessungsschicht ist noch keinem Mitarbeiter zugewiesen!",
        }
      }
    },
    getShiftTypeStreak(candidate, dateStr, currentShiftType) {
      if (!candidate || !dateStr || !currentShiftType) return 0
      const empId = String(candidate._id)
      const empRecord = this.employees.find((e) => String(e.user?._id || e.user) === empId)
      if (!empRecord) return 0

      let streak = 0
      let checkDate = this.$moment(dateStr).subtract(1, "day")

      for (let i = 0; i < 10; i++) {
        const checkStr = checkDate.format("YYYY-MM-DD")
        const matchingShift = this.shifts.find((s) => {
          if (!s.assignedEmployee || s.date !== checkStr) return false
          const assignedId = String(s.assignedEmployee._id || s.assignedEmployee)
          return assignedId === empId
        })

        if (matchingShift) {
          const sTimes =
            matchingShift.times && matchingShift.times.length > 0
              ? matchingShift.times
              : matchingShift.shiftTemplate
              ? matchingShift.shiftTemplate.times
              : []
          const sType = this.determineShiftType(matchingShift, sTimes)
          if (sType === currentShiftType) {
            streak++
            checkDate.subtract(1, "day")
          } else {
            break
          }
        } else {
          break
        }
      }
      return streak
    },
    getConflictGerman(conflict) {
      if (!conflict) return "Konflikt"
      return conflict
        .replace("Mitarbeiter hat Freiwunsch", "Freiwunsch")
        .replace("Mitarbeiter hat Ferien", "Ferien/Urlaub")
        .replace("Berufsschule", "Berufsschule")
        .replace("Wiederkehrender Sperrtag", "wiederkehrender Sperrtag")
        .replace("Anderes Studio (Einsatz auswärts)", "Einsatz in anderem Studio")
        .replace("Bereits einer anderen Schicht an diesem Tag zugeteilt", "anderweitige Schichteinteilung")
        .replace("Arbeitet bereits in einer anderen Instanz dieser Stammschicht", "arbeitet in anderer Instanz dieser Stammschicht")
        .replace("Arbeitsstreak verletzt (Mitarbeiter würde 6 Tage hintereinander arbeiten)", "Arbeitsstreak (6 Tage am Stück)")
        .replace("Arbeitsstreak verletzt", "Arbeitsstreak")
        .replace("Ruhezeit verletzt", "Ruhezeit verletzt")
        .replace("Kompetenz", "fehlende Qualifikation")
        .replace("Fix-Schicht Konflikt", "Stammschicht-Konflikt")
    },
    async refreshShiftCandidates(shiftId) {
      const id = String(shiftId)
      const requestVersion = (this.candidateRequestVersions[id] || 0) + 1
      const strategy = this.planningStrategy
      this.$set(this.candidateRequestVersions, id, requestVersion)
      const isCurrentRequest = () => this.candidateRequestVersions[id] === requestVersion
      const isSelectedShift = () => String(this.selectedShift?._id || "") === id

      try {
        const res = await api.shiftManagement.fetchCandidates(id, strategy)
        if (!isCurrentRequest()) return
        const candidates = res || { available: [], unavailable: [] }
        this.$set(this.weeklyCandidatesCache, id, candidates)
        if (isSelectedShift()) this.candidates = candidates
      } catch (error) {
        // Superseded requests must not clear a newer selection or report its error.
        if (isCurrentRequest()) throw error
      } finally {
        if (isCurrentRequest() && isSelectedShift()) this.loadingCandidates = false
      }
    },
    async loadWeeklyCandidatesCache() {
      const requestId = ++this.weeklyCandidatesRequestId
      if (!this.currentWeek) return
      const shiftsInWeek = this.shifts.filter((s) => s.date >= this.currentWeek.start && s.date <= this.currentWeek.end)

      const batchSize = 3
      for (let i = 0; i < shiftsInWeek.length; i += batchSize) {
        if (requestId !== this.weeklyCandidatesRequestId) return
        const batch = shiftsInWeek.slice(i, i + batchSize)
        await Promise.all(
          batch.map(async (shift) => {
            try {
              await this.refreshShiftCandidates(shift._id)
            } catch (e) {
              console.error("Cache load error for shift:", shift._id, e)
            }
          }),
        )
      }
    },
    onCandidateDragStart(event, candidate) {
      this.draggedEmployee = candidate
      event.dataTransfer.setData("text/plain", candidate._id)
      event.dataTransfer.effectAllowed = "move"
    },
    hasConflictForDay(employee, dateStr) {
      if (!employee) return false
      const employeeId = String(employee._id)
      const shiftsOnDay = this.getShiftsForDay(dateStr)
      if (shiftsOnDay.length === 0) return false
      return shiftsOnDay.some((shift) => {
        const cached = this.weeklyCandidatesCache[shift._id]
        if (!cached || !cached.unavailable) return false
        return cached.unavailable.some((u) => String(u._id) === employeeId)
      })
    },
    getDayPlannedHoursText(dateStr) {
      const dayShifts = this.getShiftsForDay(dateStr)
      const assignedShifts = dayShifts.filter((s) => s.assignedEmployee)
      const totalHours = assignedShifts.reduce((sum, s) => {
        if (!s.times) return sum
        return (
          sum +
          s.times.reduce((tSum, t) => {
            if (t.type === "Arbeitszeit" && t.start && t.end) {
              const start = this.$moment(t.start, "HH:mm")
              const end = this.$moment(t.end, "HH:mm")
              if (end.isBefore(start)) end.add(1, "day")
              return tSum + end.diff(start, "hours", true)
            }
            return tSum
          }, 0)
        )
      }, 0)
      return `${totalHours.toFixed(1)} Std.`
    },
    getDayAssignedShiftsCount(dateStr) {
      const dayShifts = this.getShiftsForDay(dateStr)
      const assignedCount = dayShifts.filter((s) => s.assignedEmployee).length
      if (assignedCount === 0) return "0 Schichten"
      if (assignedCount === 1) return "1 Schicht"
      return `${assignedCount} Schichten`
    },
    async resetWeekPlanning() {
      if (!this.studioId || !this.currentWeek) return
      this.resettingWeek = true
      try {
        const res = await api.shiftManagement.resetPlanAssignments(this.studioId, this.currentWeek.start, this.currentWeek.end)
        if (Number(res.modifiedCount) > 0) this.$emit("plan-draft")
        this.$toast.success(`${res.modifiedCount || 0} Zuweisungen dieser Woche wurden erfolgreich zurückgesetzt!`)
        this.confirmResetWeekDialog = false
        await this.fetchShifts()
      } catch (err) {
        console.error("Fehler beim Zurücksetzen der Woche:", err)
        this.$toast.error("Fehler beim Zurücksetzen der Woche.")
      } finally {
        this.resettingWeek = false
      }
    },
    async onCandidateDrop(event, shift) {
      this.draggedEmployee = null
      if (this.isShiftAssignmentPending(shift)) return
      const employeeId = event.dataTransfer.getData("text/plain")
      if (!employeeId) return

      const cached = this.weeklyCandidatesCache[shift._id]
      this.candidates = cached || { available: [], unavailable: [] }
      if (cached) {
        const isUnavailable = cached.unavailable.some((u) => String(u._id) === String(employeeId))
        if (isUnavailable) {
          const emp = cached.unavailable.find((u) => String(u._id) === String(employeeId))
          this.selectedShift = shift
          this.openOverrideDialog(emp)
          return
        }
      }

      this.selectedShift = shift
      await this.assignEmployee(employeeId, "Manuell zugewiesen (Drag & Drop)")
    },
    isShiftGap(shiftId) {
      const cached = this.weeklyCandidatesCache[shiftId]
      return cached && cached.available.length === 0
    },
    hasAssignedWish(shift) {
      if (!shift.assignedEmployee) return false
      const cached = this.weeklyCandidatesCache[shift._id]
      if (!cached) return false
      const empId = shift.assignedEmployee._id || shift.assignedEmployee
      return cached.available.some((c) => String(c._id) === String(empId) && c.hasMatchingWish)
    },
    isOverrideShift(shift) {
      return !!shift.allocationNote && shift.allocationNote.includes("Manuell überschrieben:")
    },
    getScorePercentage(score) {
      if (score <= 0) return 0
      if (score >= 10000) {
        const extra = Math.min(5, Math.floor((score - 10000) / 800))
        return Math.min(100, 95 + extra)
      } else {
        const diff = (10000 - score) / 10000
        return Math.max(10, Math.round(95 - diff * 85))
      }
    },
    getShiftsForDay(dateStr) {
      return this.shifts
        .filter((s) => s.date === dateStr && !s.isVirtualExternal)
        .sort((a, b) => {
          const getStart = (s) => {
            const t = s.times && s.times.length > 0 ? s.times : s.shiftTemplate ? s.shiftTemplate.times : []
            return t.length > 0 ? t[0].start : "00:00"
          }
          return getStart(a).localeCompare(getStart(b))
        })
    },
    formatShiftTime(shift) {
      const t = shift.times && shift.times.length > 0 ? shift.times : shift.shiftTemplate ? shift.shiftTemplate.times : []
      if (t.length === 0) return "?"
      return `${t[0].start} - ${t[t.length - 1].end}`
    },
    getShiftColor(shift) {
      return shift.shiftTemplate ? shift.shiftTemplate.color : "#E91E63"
    },
    async selectShift(shift) {
      this.selectedShift = shift
      this.drawer = true

      const cached = this.weeklyCandidatesCache[shift._id]
      if (cached) {
        this.candidates = cached
        this.loadingCandidates = false
      } else {
        this.candidates = { available: [], unavailable: [] }
        this.loadingCandidates = true
      }

      try {
        await this.refreshShiftCandidates(shift._id)
      } catch (e) {
        console.error("Candidates Load Error:", e)
        if (String(this.selectedShift?._id || "") === String(shift._id) && !cached) {
          this.$toast.error("Fehler beim Laden der Empfehlungen.")
          this.candidates = { available: [], unavailable: [] }
        }
      }
    },
    async submitAddShift() {
      if (!this.addShiftTemplateId || !this.addShiftDate) return
      this.creatingShift = true
      try {
        const tpl = this.availableShifts.find((s) => s._id === this.addShiftTemplateId)
        if (!tpl) throw new Error("Schicht-Vorlage nicht gefunden")

        const payload = {
          studio: this.studioId,
          date: this.addShiftDate,
          times: tpl.times || [],
          shiftTemplate: this.addShiftTemplateId,
          assignedEmployee: null,
          allocationNote: "Manuell hinzugefügt",
          status: "unassigned",
        }
        await api.shiftManagement.createManualShift(payload)
        this.$toast.success("Schicht manuell hinzugefügt.")
        this.addShiftDialog = false
        this.addShiftTemplateId = null
        await this.fetchShifts(true)
      } catch (err) {
        console.error("Error creating manual shift:", err)
        this.$toast.error("Fehler beim Erstellen der Schicht.")
      } finally {
        this.creatingShift = false
      }
    },
    async confirmExternalWork(employee, dateStr) {
      try {
        const otherStudio = employee.studio.find((s) => String(s._id || s) !== String(this.studioId))
        const otherStudioId = otherStudio ? otherStudio._id || otherStudio : this.studioId

        const payload = {
          studio: otherStudioId,
          date: dateStr,
          times: [],
          shiftTemplate: null,
          assignedEmployee: employee.user?._id || employee.user || employee._id,
          allocationNote: "Einsatz bestätigt (Zeiten unbestätigt)",
          status: "assigned",
          isVirtualExternal: true,
        }
        await api.shiftManagement.createManualShift(payload)
        this.$toast.success("Einsatz erfolgreich bestätigt.")
        await this.fetchShifts(true)
      } catch (err) {
        console.error("Error confirming external work:", err)
        this.$toast.error("Fehler beim Bestätigen des Einsatzes.")
      }
    },
    async cancelExternalWork(shiftId) {
      try {
        await api.shiftManagement.deleteShift(shiftId)
        this.$toast.success("Bestätigung des Einsatzes aufgehoben.")
        await this.fetchShifts(true)
      } catch (err) {
        console.error("Error cancelling external work:", err)
        this.$toast.error("Fehler beim Aufheben der Bestätigung.")
      }
    },
    async assignEmployee(employeeId, allocationNote = "Manuell zugewiesen (Assistent)", bypassSickCheck = false) {
      const shift = this.selectedShift
      if (!shift || this.isShiftAssignmentPending(shift)) return

      // Intercept if employee has partial absence
      let candidate = null
      if (this.candidates.available) {
        candidate = this.candidates.available.find((c) => String(c._id) === String(employeeId))
      }
      if (!candidate && this.candidates.unavailable) {
        candidate = this.candidates.unavailable.find((c) => String(c._id) === String(employeeId))
      }

      if (!bypassSickCheck && candidate && candidate.partialSickDetails && !this.sickAssignDialog) {
        this.sickAssignEmployee = candidate
        this.sickAssignDetails = candidate.partialSickDetails
        this.sickAssignCalculationMode = candidate.partialSickDetails.calculationMode
        this.sickAssignLocation = candidate.partialSickDetails.sickLocation
        this.sickAssignDialog = true
        return
      }

      const empRecord = this.employees.find((e) => String(e.user._id || e.user) === String(employeeId))
      const assignedEmployeeObj = empRecord
        ? {
            _id: employeeId,
            fullName: empRecord.fullName,
            picture: empRecord.picture,
          }
        : { _id: employeeId, fullName: "Mitarbeiter" }

      return this.saveShiftAssignment(
        shift,
        {
          assignedEmployee: employeeId,
          status: "assigned",
          allocationNote: allocationNote,
        },
        assignedEmployeeObj,
        "Mitarbeiter erfolgreich zugewiesen",
        "Fehler bei der Zuweisung",
      )
    },
    async removeAssignment(shift) {
      return this.saveShiftAssignment(
        shift,
        { assignedEmployee: null, status: "unassigned", allocationNote: "" },
        null,
        "Zuweisung entfernt",
        "Fehler beim Entfernen",
      )
    },
    isShiftAssignmentPending(shift) {
      return !!this.pendingShiftAssignments[String(shift?._id || "")]
    },
    async saveShiftAssignment(shift, changes, optimisticEmployee, successMessage, errorMessage) {
      const shiftId = String(shift?._id || "")
      if (!shiftId || this.isShiftAssignmentPending(shift)) return false
      const previousAssignment = {
        assignedEmployee: shift.assignedEmployee,
        allocationNote: shift.allocationNote,
        status: shift.status,
      }

      this.$set(this.pendingShiftAssignments, shiftId, true)
      Object.assign(shift, changes, { assignedEmployee: optimisticEmployee })

      try {
        const res = await api.shiftManagement.updateShift(shiftId, changes)
        const freshShift = res?.data || res
        if (!freshShift || String(freshShift._id || "") !== shiftId) {
          throw new Error("Die Speicherantwort gehört nicht zur bearbeiteten Schicht.")
        }

        // Resolve by the captured ID, never by the selection after an await.
        const currentShift = this.shifts.find((entry) => String(entry._id) === shiftId)
        Object.assign(shift, freshShift)
        if (currentShift && currentShift !== shift) Object.assign(currentShift, freshShift)
      } catch (e) {
        Object.assign(shift, previousAssignment)
        console.error("Shift Assignment Error:", e)
        this.$toast.error(errorMessage)
        return false
      } finally {
        this.$delete(this.pendingShiftAssignments, shiftId)
      }

      this.$toast.success(successMessage)
      // Recommendation failures must not undo an assignment already saved on the server.
      try {
        await this.loadWeeklyCandidatesCache()
        if (this.drawer && String(this.selectedShift?._id || "") === shiftId) {
          const currentShift = this.shifts.find((entry) => String(entry._id) === shiftId)
          if (currentShift) {
            this.selectShift(currentShift)
          } else {
            this.selectedShift = null
            this.drawer = false
          }
        }
      } catch (e) {
        console.error("Assignment Recommendations Refresh Error:", e)
      }
      return true
    },
    async fetchWeeklyTemplate() {
      try {
        const res = await api.shiftManagement.fetchWeeklyTemplate(this.studioId)
        this.weeklyTemplateData = res || []
      } catch (e) {
        console.error("Error loading weekly template:", e)
      }
    },
    openSpecialDayDialog(dateStr) {
      this.specialDayDate = dateStr
      this.specialDayMode = "replace"

      this.calculateStandardShifts(dateStr)

      const existingShifts = this.shifts.filter((s) => s.date === dateStr)
      if (existingShifts.length > 0) {
        const isSpecial = existingShifts.some((s) => s.isSpecialDemand === true || (s.allocationNote && s.allocationNote.includes("Spezialtag")))
        this.specialDayMode = isSpecial
          ? existingShifts[0].allocationNote && existingShifts[0].allocationNote.includes("Zusatz")
            ? "add"
            : "replace"
          : "replace"

        let mapped = existingShifts.map((s) => {
          const isStd = !(s.isSpecialDemand === true || (s.allocationNote && s.allocationNote.includes("Spezialtag")))
          return {
            shiftTemplateId: s.shiftTemplate ? s.shiftTemplate._id || s.shiftTemplate : null,
            assignedUserId: s.assignedEmployee ? s.assignedEmployee._id || s.assignedEmployee : null,
            note: s.allocationNote || "",
            times: s.times && s.times.length > 0 ? JSON.parse(JSON.stringify(s.times)) : [{ start: "08:00", end: "12:00", type: "Arbeitszeit" }],
            isStandard: isStd,
          }
        })

        if (this.specialDayMode === "add") {
          mapped = mapped.filter((s) => !s.isStandard)
        }
        this.specialDayShifts = mapped
      } else {
        this.specialDayShifts = JSON.parse(JSON.stringify(this.standardShiftsForDate))
      }

      this.specialDayDialog = true
    },
    calculateStandardShifts(dateStr) {
      const isoWeekday = this.$moment(dateStr).isoWeekday()
      const dayTemplate = this.weeklyTemplateData.find((d) => d.dayOfWeek === isoWeekday)

      this.standardShiftsForDate = []

      if (dayTemplate && dayTemplate.requiredShifts) {
        this.standardShiftsForDate = dayTemplate.requiredShifts.map((req) => {
          const tpl = this.availableShifts.find((s) => s._id === (req.shiftTemplateId._id || req.shiftTemplateId))
          let times = [{ start: "00:00", end: "00:00", type: "Arbeitszeit" }]
          if (tpl && tpl.times) times = JSON.parse(JSON.stringify(tpl.times))

          return {
            shiftTemplateId: req.shiftTemplateId._id || req.shiftTemplateId,
            assignedUserId: null,
            note: "",
            times: times,
            isStandard: true,
          }
        })
      }
    },
    addSpecialDayShift() {
      this.specialDayShifts.push({
        shiftTemplateId: null,
        assignedUserId: null,
        note: "",
        times: [{ start: "08:00", end: "12:00", type: "Arbeitszeit" }],
      })
    },
    addSpecialDayTimeBlock(shift) {
      const last = shift.times[shift.times.length - 1]
      const newType = last.type === "Arbeitszeit" ? "Pause" : "Arbeitszeit"
      shift.times.push({
        start: last.end,
        end: this.$moment(last.end, "HH:mm").add(30, "minutes").format("HH:mm"),
        type: newType,
      })
    },
    removeSpecialDayTimeBlock(shift, index) {
      shift.times.splice(index, 1)
    },
    copySpecialDayShift(shift) {
      const copy = JSON.parse(JSON.stringify(shift))
      copy.assignedUserId = null
      this.specialDayShifts.push(copy)
    },
    removeSpecialDayShift(index) {
      this.specialDayShifts.splice(index, 1)
    },
    applySpecialDayTemplate(shift, templateId) {
      const template = this.availableShifts.find((t) => t._id === templateId)
      if (template && template.times && template.times.length > 0) {
        shift.times = JSON.parse(JSON.stringify(template.times))
      }
    },
    loadSpecialDayStandardShifts() {
      this.specialDayShifts = JSON.parse(JSON.stringify(this.standardShiftsForDate))
      this.$toast.info("Standard-Schichten geladen")
    },
    async saveSpecialDay() {
      if (!this.specialDayDate) return this.$toast.error("Bitte Datum wählen")
      if (this.specialDayMode === "add" && this.specialDayShifts.length === 0) {
        return this.$toast.error("Für 'Zusatz' bitte mindestens eine Schicht definieren.")
      }

      this.savingSpecialDay = true
      try {
        const payload = {
          studio: this.studioId,
          date: this.specialDayDate,
          mode: this.specialDayMode,
          shifts: this.specialDayShifts,
        }

        const updatedShifts = await api.shiftManagement.createSpecialDay(payload)

        this.shifts = this.shifts.filter((s) => s.date !== this.specialDayDate)
        this.shifts.push(...(updatedShifts || []))

        this.$toast.success("Spezialtag erfolgreich gespeichert")
        await this.loadWeeklyCandidatesCache()
        this.specialDayDialog = false
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern des Spezialtags")
      } finally {
        this.savingSpecialDay = false
      }
    },
    getEmployeeStatus(employee, dateStr) {
      if (!employee || !dateStr) return []
      const status = []
      const targetDate = this.$moment(dateStr).format("YYYY-MM-DD")
      const dayOfWeekISO = this.$moment(dateStr).isoWeekday()
      const effectiveSettings = this.getEffectiveEmployeePlanningSettings(employee, targetDate)

      // 1. Ferien
      const vacations = employee.vacations || []
      const isOnVacation = vacations.some((vac) => (vac.dates || []).some((d) => this.$moment(d.date).format("YYYY-MM-DD") === targetDate))
      if (isOnVacation) {
        status.push("Ferien")
      }

      // 2. Berufsschule
      const isApprentice = typeof effectiveSettings.isApprentice === "boolean" ? effectiveSettings.isApprentice : employee.isApprentice
      if (isApprentice) {
        const isSchoolHoliday = (this.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && this.$moment(dateStr).isBetween(sh.startDate, sh.endDate, null, "[]"),
        )
        if (!isSchoolHoliday) {
          const schoolDays = Array.isArray(effectiveSettings.schoolDays) ? effectiveSettings.schoolDays : employee.schoolDays || []
          const isSchoolDay = schoolDays.some((sd) => Number(sd?.dayOfWeek ?? sd) === dayOfWeekISO)
          if (isSchoolDay) {
            status.push("Schule")
          }
        }
      }

      // 3. Freiwunsch (freeWishes)
      const freeWishes = employee.freeWishes || []
      const hasFreeWish = freeWishes.some((fw) => this.$moment(fw.day).format("YYYY-MM-DD") === targetDate)
      if (hasFreeWish) {
        status.push("Freiwunsch")
      }

      // 4. Wiederkehrender Sperrtag (recurringAbsence)
      const recurringAbsence = effectiveSettings.recurringAbsence || employee.planningSettings?.recurringAbsence || []
      if (recurringAbsence.includes(dayOfWeekISO)) {
        status.push("Sperrtag")
      }

      // 5. Abwesenheit (Krank, Militär, etc.)
      const absences = employee.sickEntries || []
      const matchedAbs = absences.find(
        (abs) => abs.status !== "rejected" && abs.sickDays && abs.sickDays.some((d) => this.$moment(d.date).format("YYYY-MM-DD") === targetDate),
      )
      if (matchedAbs) {
        const reasonLabels = {
          sick: "Krank",
          school: "Schule",
          military: "Militär",
          pregnancy: "Schwangerschaft",
          other: "Abwesenheit",
        }
        status.push(reasonLabels[matchedAbs.absenceReason] || "Abwesenheit")
      }

      // 5. Externer Einsatz
      const recurringExternal = effectiveSettings.recurringExternalStudio || employee.planningSettings?.recurringExternalStudio || []
      if (recurringExternal.includes(dayOfWeekISO)) {
        status.push("Extern")
      }

      return status
    },
    getShiftName(templateId) {
      if (!templateId) return "Spezial-Schicht"
      const templateVal = templateId._id || templateId
      const tpl = this.availableShifts.find((s) => s._id === templateVal)
      return tpl ? tpl.name : "Unbekannte Vorlage"
    },
    getShiftTimeRange(shift) {
      if (!shift.times || shift.times.length === 0) return "?"
      return `${shift.times[0].start} - ${shift.times[shift.times.length - 1].end}`
    },
    getActiveEmployeeNotes() {
      if (!this.activeNoteEmployee || !this.activeNoteDate) return []
      const empId = this.activeNoteEmployee._id || this.activeNoteEmployee.user?._id || this.activeNoteEmployee.user
      return this.notes.filter((n) => String(n.targetEmployee) === String(empId) && n.date === this.activeNoteDate)
    },
    openNoteDialog(employee, date) {
      this.activeNoteEmployee = employee
      this.activeNoteDate = date
      this.newNoteText = ""
      this.noteDialog = true
    },
    async submitAddNote() {
      if (!this.activeNoteEmployee || !this.activeNoteDate || !this.newNoteText.trim()) return
      this.savingNote = true
      try {
        const empId = this.activeNoteEmployee._id || this.activeNoteEmployee.user?._id || this.activeNoteEmployee.user
        const payload = {
          date: this.activeNoteDate,
          targetEmployee: empId,
          content: this.newNoteText.trim(),
        }
        await api.shiftManagement.createNote(payload)

        // Reload all notes in the background
        const notesRes = await api.shiftManagement.fetchNotes(this.studioId, this.startDate, this.endDate)
        this.notes = notesRes || []

        this.$toast.success("Notiz erfolgreich hinzugefügt")
        this.newNoteText = ""
      } catch (e) {
        console.error("Add Note Error:", e)
        this.$toast.error("Fehler beim Hinzufügen der Notiz")
      } finally {
        this.savingNote = false
      }
    },
    async deleteNote(noteId) {
      try {
        await api.shiftManagement.deleteNote(noteId)
        this.notes = this.notes.filter((n) => n._id !== noteId)
        this.$toast.success("Notiz gelöscht")
      } catch (e) {
        console.error("Delete Note Error:", e)
        this.$toast.error("Fehler beim Löschen der Notiz")
      }
    },
    openOverrideDialog(employee) {
      this.selectedOverrideEmployee = employee
      this.overrideReason = null
      this.customOverrideReason = ""
      this.overrideReasonDialog = true
    },
    submitOverride() {
      if (!this.selectedOverrideEmployee || !this.selectedShift) return
      this.assignEmployee(this.selectedOverrideEmployee._id, "Manuell zugewiesen")
      this.overrideReasonDialog = false
    },
    async submitSickAssign() {
      if (!this.sickAssignEmployee || !this.sickAssignDetails) return

      this.sickAssignDialog = false
      const employeeId = this.sickAssignEmployee._id
      const date = this.sickAssignDetails.date
      const mode = this.sickAssignCalculationMode
      const loc = this.sickAssignLocation

      try {
        await api.shiftManagement.updateAbsenceMode({
          employeeId,
          date,
          calculationMode: mode,
          sickLocation: loc,
        })
        await this.assignEmployee(employeeId, "Manuell zugewiesen (Assistent)", true)
      } catch (err) {
        console.error("Error submitting sick assign:", err)
        this.$toast.error("Fehler beim Aktualisieren der Abwesenheit und Zuweisen")
      }
    },
    async autoFillWeek() {
      if (!this.currentWeek) return
      this.autoFilling = true
      try {
        const payload = {
          studio: this.studioId,
          startDate: this.currentWeek.start,
          endDate: this.currentWeek.end,
          planningSettings: {},
          confirmedEmployeeUserIds: this.employees
            .map((e) => {
              if (!e) return null
              const u = e.user
              if (!u) return null
              return typeof u === "object" ? u._id || u : u
            })
            .filter(Boolean),
        }

        await api.shiftManagement.assignEmployeesToPlan(
          this.studioId,
          this.currentWeek.start,
          this.currentWeek.end,
          payload.planningSettings,
          payload.confirmedEmployeeUserIds,
          this.planningStrategy,
        )

        this.$toast.success("Restliche Schichten der Woche erfolgreich automatisch gefüllt!")
        await this.fetchShifts(true)
        this.drawer = false
      } catch (e) {
        console.error("Auto Fill Week Error:", e)
        this.$toast.error("Fehler beim Auto-Füllen der Woche")
      } finally {
        this.autoFilling = false
      }
    },
    nextWeek() {
      this.currentWeekIndex++
      this.drawer = false
      this.loadWeeklyCandidatesCache()
    },
    prevWeek() {
      this.currentWeekIndex--
      this.drawer = false
      this.loadWeeklyCandidatesCache()
    },
    openAddShiftDialog(dateStr) {
      this.addShiftDate = dateStr
      this.addShiftTemplateId = null
      this.addShiftDialog = true
    },
    async deleteShift(shift) {
      if (this.isShiftAssignmentPending(shift)) return
      if (confirm("Schicht wirklich löschen?")) {
        try {
          await api.shiftManagement.deleteShift(shift._id)
          this.$toast.success("Schicht gelöscht")
          if (this.selectedShift && this.selectedShift._id === shift._id) {
            this.selectedShift = null
          }
          await this.fetchShifts(true)
        } catch (e) {
          console.error("Error deleting shift:", e)
          this.$toast.error("Fehler beim Löschen der Schicht.")
        }
      }
    },
    finishPlanning() {
      // Zähle unbesetzte Schichten im Monat
      const unassigned = this.shifts.filter((s) => !s.assignedEmployee)
      if (unassigned.length > 0) {
        this.unassignedCountInMonth = unassigned.length
        this.finishWarningDialog = true
      } else {
        this.$emit("view-plan")
      }
    },
    async deleteUnassignedAndFinish() {
      this.deletingUnassigned = true
      try {
        await api.shiftManagement.deleteUnassignedShifts(this.studioId, this.startDate, this.endDate)
        this.$toast.success("Alle unbesetzten Schichten wurden gelöscht.")
        this.finishWarningDialog = false
        this.$emit("view-plan")
      } catch (err) {
        console.error("Fehler beim Löschen der unbesetzten Schichten:", err)
        this.$toast.error("Fehler beim Löschen der Schichten.")
      } finally {
        this.deletingUnassigned = false
      }
    },
    determineShiftCategories(shift) {
      const times = shift.times && shift.times.length > 0 ? shift.times : shift.shiftTemplate ? shift.shiftTemplate.times : []
      const name = String(shift.shiftTemplate?.name || shift.name || "")
        .trim()
        .toLowerCase()

      // Check if split shift (Zimmerstunde)
      let hasZimmerstunde = false
      const workSegments = (times || [])
        .filter((time) => time.type === "Arbeitszeit" && time.start && time.end)
        .slice()
        .sort((left, right) => String(left.start).localeCompare(String(right.start)))
      if (workSegments.length > 0) {
        if (workSegments.length > 1) hasZimmerstunde = true

        let totalPauseMinutes = 0
        times.forEach((t) => {
          if (t.type === "Pause" && t.start && t.end) {
            const s = this.$moment(t.start, "HH:mm")
            const e = this.$moment(t.end, "HH:mm")
            if (e.isBefore(s)) e.add(1, "day")
            totalPauseMinutes += e.diff(s, "minutes")
          }
        })
        if (totalPauseMinutes >= 90) {
          // 1.5 hours or more
          hasZimmerstunde = true
        }
      }

      if (
        hasZimmerstunde ||
        name.includes("zwischen") ||
        name.includes("zimmer") ||
        name.includes("split") ||
        name.includes("intermediate") ||
        name.includes("geteilt") ||
        name.includes("mittel")
      ) {
        return ["intermediate"]
      }
      if (name.includes("früh") || name.includes("frueh") || name.includes("morning") || name.includes("öffnung") || name.includes("oeffnung")) {
        return ["morning"]
      }
      if (name.includes("spät") || name.includes("spaet") || name.includes("late") || name.includes("schliess") || name.includes("closing")) {
        return ["late"]
      }

      const isOpeningShift =
        shift.isOpeningShift !== undefined && shift.isOpeningShift !== null
          ? Boolean(shift.isOpeningShift)
          : Boolean(shift.shiftTemplate?.isOpeningShift)
      const isClosingShift =
        shift.isClosingShift !== undefined && shift.isClosingShift !== null
          ? Boolean(shift.isClosingShift)
          : Boolean(shift.shiftTemplate?.isClosingShift)
      if (isOpeningShift && !isClosingShift) return ["morning"]
      if (isClosingShift && !isOpeningShift) return ["late"]

      if (workSegments.length > 0) {
        const toMinutes = (value) => {
          const [hours, minutes] = String(value)
            .split(":")
            .map((part) => Number(part))
          return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null
        }
        const startMinutes = toMinutes(workSegments[0].start)
        let endMinutes = toMinutes(workSegments[workSegments.length - 1].end)
        if (startMinutes !== null && endMinutes !== null) {
          if (endMinutes < startMinutes) endMinutes += 24 * 60
          if (endMinutes <= 18 * 60 + 30) return ["morning"]
          if (startMinutes >= 12 * 60 || endMinutes >= 21 * 60) return ["late"]
        }
      }
      return ["intermediate"]
    },
    getEmployeeStats(employeeId) {
      const empIdStr = String(employeeId)
      const empShifts = this.shifts.filter((s) => s.assignedEmployee && String(s.assignedEmployee._id || s.assignedEmployee) === empIdStr)

      let morning = 0
      let intermediate = 0
      let late = 0

      empShifts.forEach((s) => {
        const cats = this.determineShiftCategories(s)
        if (cats.includes("morning")) morning++
        if (cats.includes("intermediate")) intermediate++
        if (cats.includes("late")) late++
      })

      return {
        morning,
        intermediate,
        late,
        total: empShifts.length,
      }
    },
    getEmployeePercentage(employee) {
      if (!employee) return 0
      const studioEntry = employee.studio?.find((s) => String(s._id) === String(this.studioId))

      const effectiveSettings = this.getEffectiveEmployeePlanningSettings(employee, this.startDate)
      const manualPercentage = effectiveSettings.percentages?.[this.studioId]
      if (manualPercentage !== undefined && manualPercentage !== null && manualPercentage !== "") {
        const monthlyPercentage = Number(String(manualPercentage).replace(",", "."))
        if (Number.isFinite(monthlyPercentage)) return Math.max(0, monthlyPercentage)
      }

      if (studioEntry?.percentage === undefined || studioEntry?.percentage === null || studioEntry?.percentage === "") return 0
      const statisticPercentage = Number(String(studioEntry.percentage).replace(",", "."))
      return Number.isFinite(statisticPercentage) ? Math.max(0, statisticPercentage) : 0
    },
    getEmployeePlannedHours(employeeId) {
      const empId = String(employeeId)
      return this.shifts
        .filter((s) => s.assignedEmployee && String(s.assignedEmployee._id || s.assignedEmployee) === empId)
        .reduce((sum, s) => {
          const times = s.times && s.times.length > 0 ? s.times : s.shiftTemplate ? s.shiftTemplate.times : []
          return (
            sum +
            times.reduce((total, time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const start = this.$moment(time.start, "HH:mm")
                const end = this.$moment(time.end, "HH:mm")
                if (end.isBefore(start)) end.add(1, "day")
                return total + end.diff(start, "hours", true)
              }
              return total
            }, 0)
          )
        }, 0)
    },
    getHoursPercentage(employee) {
      const target = this.calculateSollStunden(employee)
      if (target <= 0) return 0
      const userId = String(employee.user?._id || employee.user || employee._id)
      const planned = this.getEmployeePlannedHours(userId)
      return Math.min(100, Math.round((planned / target) * 100))
    },
    getHoursColor(employee) {
      const p = this.getHoursPercentage(employee)
      if (p < 80) return "orange lighten-1"
      if (p <= 100) return "green accent-3"
      return "red accent-3"
    },
    getEmployeeWeeklyShifts(employeeId) {
      if (!this.currentWeek) return []
      const empId = String(employeeId)
      return this.shifts.filter(
        (s) =>
          s.assignedEmployee &&
          String(s.assignedEmployee._id || s.assignedEmployee) === empId &&
          s.date >= this.currentWeek.start &&
          s.date <= this.currentWeek.end,
      )
    },
    resolveEmployee(assignedEmployee) {
      if (!assignedEmployee) return null
      const id = String(assignedEmployee._id || assignedEmployee)
      const found = this.employees.find((e) => String(e.user?._id || e.user) === id)

      let fullName = ""
      if (found && found.fullName) {
        fullName = found.fullName
      } else if (found && found.user && typeof found.user === "object" && (found.user.vorname || found.user.nachname)) {
        fullName = `${found.user.vorname || ""} ${found.user.nachname || ""}`.trim()
      } else if (assignedEmployee && typeof assignedEmployee === "object") {
        if (assignedEmployee.fullName) {
          fullName = assignedEmployee.fullName
        } else if (assignedEmployee.vorname || assignedEmployee.nachname) {
          fullName = `${assignedEmployee.vorname || ""} ${assignedEmployee.nachname || ""}`.trim()
        }
      }
      if (!fullName) {
        fullName = "Mitarbeiter"
      }

      if (found) {
        return {
          ...found,
          fullName,
        }
      }

      const fallbackEmployee = typeof assignedEmployee === "object" ? assignedEmployee : {}
      return {
        ...fallbackEmployee,
        _id: id,
        fullName,
        planningSettings: fallbackEmployee.planningSettings || {},
      }
    },
    calculateSollStunden(employee) {
      return this.getStudioHoursProjection(employee).targetHours
    },
    async preAssignFixedShifts() {
      if (!this.currentWeek) return

      const unassigned = this.shifts.filter((s) => !s.assignedEmployee && s.date >= this.currentWeek.start && s.date <= this.currentWeek.end)

      if (unassigned.length === 0) {
        this.$toast.info("Alle Schichten in dieser Woche sind bereits besetzt.")
        return
      }

      this.autoFilling = true
      let assignedCount = 0

      try {
        const assignmentsToMake = []
        const assignedEmployeeDates = new Set()
        this.shifts.forEach((s) => {
          if (s.assignedEmployee) {
            const empId = String(s.assignedEmployee._id || s.assignedEmployee)
            assignedEmployeeDates.add(`${empId}_${s.date}`)
          }
        })

        for (const shift of unassigned) {
          if (!shift.shiftTemplate) continue

          const weekday = this.$moment(shift.date).isoWeekday()
          const templateId = String(shift.shiftTemplate._id || shift.shiftTemplate)

          const cache = this.weeklyCandidatesCache[shift._id]
          if (!cache || !cache.available) continue

          const matchingEmployee = cache.available.find((candidate) => {
            const empId = String(candidate._id)
            if (assignedEmployeeDates.has(`${empId}_${shift.date}`)) return false

            const empRecord = this.employees.find((e) => String(e.user?._id || e.user) === empId)
            if (!empRecord) return false

            const savedSettings = this.getEffectiveEmployeePlanningSettings(empRecord, shift.date)
            const fixedShifts = savedSettings.fixedShifts || empRecord.planningSettings?.fixedShifts || {}

            return String(fixedShifts[weekday]) === templateId
          })

          if (matchingEmployee) {
            assignmentsToMake.push({
              shiftId: shift._id,
              employeeId: matchingEmployee._id,
              fullName: matchingEmployee.fullName,
              picture: matchingEmployee.picture,
            })
            assignedEmployeeDates.add(`${matchingEmployee._id}_${shift.date}`)
          }
        }

        if (assignmentsToMake.length === 0) {
          this.$toast.info("Keine verfügbaren Mitarbeiter mit passenden fixen Schichten für diese Woche gefunden.")
          return
        }

        for (const assign of assignmentsToMake) {
          const targetShift = this.shifts.find((s) => s._id === assign.shiftId)
          if (!targetShift) continue

          const oldEmployee = targetShift.assignedEmployee
          const oldNote = targetShift.allocationNote
          const oldStatus = targetShift.status

          // Optimistic Update
          targetShift.assignedEmployee = {
            _id: assign.employeeId,
            fullName: assign.fullName,
            picture: assign.picture,
          }
          targetShift.allocationNote = "Fixe Schicht (Automatisch)"
          targetShift.status = "assigned"

          try {
            await api.shiftManagement.updateShift(assign.shiftId, {
              assignedEmployee: assign.employeeId,
              status: "assigned",
              allocationNote: "Fixe Schicht (Automatisch)",
            })
            assignedCount++
          } catch (err) {
            targetShift.assignedEmployee = oldEmployee
            targetShift.allocationNote = oldNote
            targetShift.status = oldStatus
            console.error("Batch assignment error for shift:", assign.shiftId, err)
          }
        }

        if (assignedCount > 0) {
          this.$toast.success(`${assignedCount} fixe Schichten wurden erfolgreich zugewiesen!`)
          await this.fetchShifts(true)
        } else {
          this.$toast.error("Fehler beim Zuweisen der fixen Schichten.")
        }
      } catch (err) {
        console.error("preAssignFixedShifts Error:", err)
        this.$toast.error("Ein Fehler ist aufgetreten.")
      } finally {
        this.autoFilling = false
      }
    },
  },
}
</script>

<style scoped>
.assistant-root {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #121212;
}

/* === GLOWING CONFLICT ROW === */
.glowing-conflict-row {
  border: 1px solid #f44336 !important;
  background: rgba(244, 67, 54, 0.06) !important;
  box-shadow: 0 0 10px rgba(244, 67, 54, 0.15) !important;
}

/* === INACTIVE SECTION WHEN ASSIGNED === */
.assigned-inactive-section {
  opacity: 0.3;
  transition: opacity 0.2s ease-in-out;
}
.assigned-inactive-section:hover {
  opacity: 0.95;
}

/* === WEEK SUMMARY WIDGET === */
.week-summary-widget {
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  transition: border-color 0.2s ease;
}
.week-summary-widget:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.summary-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}
.day-coverage-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
}
.day-coverage-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
  align-items: flex-start !important;
  text-align: left !important;
}
.day-coverage-card.coverage-perfect {
  background: rgba(76, 175, 80, 0.18) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
}
.day-coverage-card.coverage-partial {
  background: rgba(255, 152, 0, 0.18) !important;
  border-color: rgba(255, 152, 0, 0.4) !important;
}
.day-coverage-card.coverage-empty {
  background: rgba(244, 67, 54, 0.18) !important;
  border-color: rgba(244, 67, 54, 0.4) !important;
}
.day-coverage-card.coverage-none {
  background: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

/* === HEADER BAR === */
.assistant-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 14px 24px;
  background: rgba(18, 18, 18, 0.6) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.assistant-header-layout {
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.assistant-identity {
  min-width: 0;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
}
.assistant-header-controls {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.assistant-week-navigation,
.assistant-status-controls {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.assistant-week-navigation,
.assistant-status-controls,
.staffing-bar,
.strategy-selector,
.assistant-actions {
  flex: 0 0 auto;
}
.assistant-week-navigation,
.staffing-bar,
.strategy-selector {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}
.staffing-bar {
  gap: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}
.staffing-bar .v-progress-linear {
  flex: 0 0 70px;
}
.strategy-selector {
  gap: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}
.assistant-actions {
  width: 160px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.assistant-header-divider,
.assistant-status-divider {
  min-height: 28px;
  align-self: center;
  flex: 0 0 auto;
  margin: 4px 2px;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.week-pill {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}
.assistant-header-controls ::v-deep .v-btn__content {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}
.assistant-back-btn {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  transition: all 0.2s ease !important;
}
.assistant-back-btn:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.assistant-title {
  font-size: 1.2rem !important;
  font-weight: 800 !important;
  color: white;
  letter-spacing: -0.3px;
}
.week-fraction-badge {
  background: rgba(63, 81, 181, 0.12) !important;
  border: 1px solid rgba(63, 81, 181, 0.25) !important;
  color: #8c9eff !important;
  border-radius: 12px;
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px;
  padding: 3px 12px !important;
}
.assistant-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 1px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
.nav-btn {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  transition: all 0.2s ease !important;
}
.nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.week-pill {
  background: rgba(102, 187, 106, 0.06) !important;
  border: 1px solid rgba(102, 187, 106, 0.2) !important;
  border-radius: 10px;
  color: #81c784 !important;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
}
.auto-fill-btn {
  background: linear-gradient(135deg, #1e88e5, #1565c0) !important;
  text-transform: none !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.25) !important;
}
.finish-assistant-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  text-transform: none !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25) !important;
}

/* Tablet headers use deliberate rows instead of squeezing labels letter by letter. */
@media (max-width: 1399px) {
  .assistant-bar {
    padding: 10px 16px;
  }
  .assistant-header-layout {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .assistant-identity {
    gap: 10px;
  }
  .assistant-header-controls {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 8px 12px;
  }
  .assistant-header-divider {
    display: none;
  }
  .assistant-actions {
    margin-left: auto;
  }
}

@media (max-width: 760px) {
  .assistant-week-navigation {
    width: 100%;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
  }
  .assistant-week-navigation .week-pill {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
  }
  .assistant-status-controls {
    width: 100%;
    flex-wrap: wrap;
  }
  .assistant-status-divider {
    display: none;
  }
  .assistant-actions {
    width: 100%;
    margin-left: 0;
    flex-direction: row;
  }
  .assistant-actions > * {
    flex: 1 1 0;
    min-width: 0;
  }
}

/* === MAIN CONTENT === */
.assistant-body {
  flex: 1;
}

/* Day Rows */
.weekly-days-list {
  max-width: 1300px;
  margin: 0 auto;
}
.weekly-day-row {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  transition: all 0.25s ease;
}
.weekly-day-row:hover {
  background: rgba(255, 255, 255, 0.045);
  border-color: rgba(255, 255, 255, 0.08);
}
.weekend-row {
  background: rgba(255, 152, 0, 0.015);
  border-color: rgba(255, 152, 0, 0.03);
}
.weekend-row:hover {
  background: rgba(255, 152, 0, 0.03);
  border-color: rgba(255, 152, 0, 0.06);
}
.today-row {
  border: 1px solid rgba(102, 187, 106, 0.25) !important;
  background: rgba(102, 187, 106, 0.02) !important;
  box-shadow: inset 0 0 10px rgba(102, 187, 106, 0.03);
}

.border-right {
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

/* Shift Card */
.shift-card {
  position: relative;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px !important;
  min-height: 54px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.delete-shift-x-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 82, 82, 0.3) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4) !important;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s, transform 0.2s;
  transform: scale(0.9);
}
.shift-card:hover .delete-shift-x-btn {
  opacity: 1;
  transform: scale(1);
}
.delete-shift-x-btn:hover {
  background: #ff5252 !important;
  color: white !important;
}
.shift-unassigned {
  border-color: rgba(255, 152, 0, 0.12) !important;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.02), rgba(255, 152, 0, 0.01)) !important;
}
.shift-unassigned:hover {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.06), rgba(255, 152, 0, 0.02)) !important;
  border-color: rgba(255, 152, 0, 0.3) !important;
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.05) !important;
}
.shift-assigned {
  border-color: rgba(255, 255, 255, 0.05) !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.01)) !important;
}
.shift-assigned:hover {
  border-color: rgba(255, 255, 255, 0.15) !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02)) !important;
  box-shadow: 0 4px 16px rgba(255, 255, 255, 0.03) !important;
}
.shift-selected {
  border-color: #448aff !important;
  background: linear-gradient(135deg, rgba(68, 138, 255, 0.08), rgba(68, 138, 255, 0.02)) !important;
  box-shadow: 0 0 16px rgba(68, 138, 255, 0.15) !important;
}
.pulse-hover:hover {
  /* controlled by individual hover overrides */
}
.border-left-dashed {
  border-left: 1px dashed rgba(255, 255, 255, 0.1);
}
.border-top-dashed {
  border-top: 1px dashed rgba(255, 255, 255, 0.1);
}
.truncate-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hover-show {
  opacity: 0;
  transition: opacity 0.2s;
}
.shift-card:hover .hover-show {
  opacity: 1;
}

/* === DRAWER === */
.assistant-drawer {
  width: clamp(480px, 32vw, 560px);
  flex: 0 0 clamp(480px, 32vw, 560px);
  height: 100%;
  z-index: 100;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.5);
  animation: slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.border-bottom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.border-left {
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}
.section-title {
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 0.5px;
}

/* Recommendation Card */
.reco-card {
  background: rgba(102, 187, 106, 0.03) !important;
  border: 1px solid rgba(102, 187, 106, 0.2) !important;
  cursor: pointer;
  transition: all 0.2s ease;
}
.reco-card:hover {
  background: rgba(102, 187, 106, 0.05) !important;
  border-color: rgba(102, 187, 106, 0.35) !important;
  transform: translateY(-1px);
}
.border-reco {
  border: 2px solid #66bb6a;
}

/* Unavailable Card list */
.border-danger-left {
  border-left: 4px solid #b71c1c !important;
}
.opacity-semi {
  opacity: 0.6;
}

/* Transitions */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #444;
}

/* Premium Open Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.status-open {
  background: rgba(255, 152, 0, 0.12) !important;
  color: #ff9800 !important;
  border: 1px solid rgba(255, 152, 0, 0.25) !important;
}
.status-danger {
  background: rgba(211, 47, 47, 0.12) !important;
  color: #ff5252 !important;
  border: 1px solid rgba(211, 47, 47, 0.25) !important;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #ff9800;
  box-shadow: 0 0 6px #ff9800;
  animation: pulseOpen 2s infinite;
}
@keyframes pulseOpen {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}

/* Premium styling overrides */
.reco-card {
  background: rgba(76, 175, 80, 0.04) !important;
  border: 1px solid rgba(76, 175, 80, 0.25) !important;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.08) !important;
}
.reco-card:hover {
  background: rgba(76, 175, 80, 0.07) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
}
.weekly-day-row {
  background: rgba(255, 255, 255, 0.02) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}
.weekly-day-row:hover {
  background: rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.today-row {
  border: 1px solid rgba(102, 187, 106, 0.3) !important;
  box-shadow: 0 0 16px rgba(102, 187, 106, 0.05) !important;
}
.shift-card {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}
.border-danger-left {
  border-left: 4px solid #d32f2f !important;
}
.border-danger {
  border: 1.5px solid rgba(211, 47, 47, 0.4) !important;
}

/* Premium Neon Glassmorphism Drawer Styling */
.assistant-drawer {
  background: rgba(18, 18, 18, 0.76) !important;
  backdrop-filter: blur(35px) saturate(210%);
  border-left: 1px solid rgba(255, 255, 255, 0.08) !important;
  width: clamp(480px, 32vw, 560px) !important;
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.6) !important;
}
.drawer-header {
  background: rgba(25, 25, 25, 0.55) !important;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.reco-card {
  background: rgba(76, 175, 80, 0.05) !important;
  border: 1px solid rgba(76, 175, 80, 0.28) !important;
  box-shadow: 0 8px 32px rgba(76, 175, 80, 0.12), inset 0 0 10px rgba(76, 175, 80, 0.05) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.reco-card:hover {
  background: rgba(76, 175, 80, 0.08) !important;
  border-color: rgba(76, 175, 80, 0.45) !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(76, 175, 80, 0.2), inset 0 0 12px rgba(76, 175, 80, 0.1) !important;
}
.border-reco {
  border: 2px solid #66bb6a !important;
  box-shadow: 0 0 8px rgba(102, 187, 106, 0.4) !important;
}
.hover-row {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.hover-row:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}
.weekly-days-list {
  max-width: 1300px !important;
}
.border-bottom-dashed {
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}
.hover-show {
  opacity: 0;
  transition: opacity 0.2s ease;
}
.weekly-day-row:hover .hover-show,
.shift-card:hover .hover-show,
.hover-row:hover .hover-show {
  opacity: 1 !important;
}

/* SPECIAL DAY DIALOG SPECIFIC UTILS */
.border-subtle {
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.border-blue {
  border: 1px solid #1e88e5 !important;
}
.border-green {
  border: 1px solid #4caf50 !important;
}
.border-right-subtle {
  border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.border-bottom-subtle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.bg-stripes {
  background: repeating-linear-gradient(45deg, #1e1e1e, #1e1e1e 10px, #252525 10px, #252525 20px);
}
@media (min-width: 960px) {
  .border-right-responsive {
    border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
  }
}
.cursor-pointer {
  cursor: pointer;
}
.time-input {
  background: transparent;
  border: none;
  color: white;
  width: 60px;
  text-align: center;
  font-family: monospace;
  font-size: 0.95rem;
  outline: none;
}
.time-input:focus {
  border-bottom: 1px solid #1e88e5;
}

/* --- PREMIUM ADVANCED ASSISTANT STYLING --- */
.fixed-fill-btn {
  background: linear-gradient(135deg, #3f51b5, #5c6bc0) !important;
  color: white !important;
  text-transform: none !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(63, 81, 181, 0.25) !important;
  transition: all 0.3s ease !important;
}
.fixed-fill-btn:hover {
  box-shadow: 0 6px 16px rgba(63, 81, 181, 0.45) !important;
  transform: translateY(-1px);
}

.premium-fixed-alert {
  box-shadow: 0 8px 24px rgba(63, 81, 181, 0.15), inset 0 0 10px rgba(63, 81, 181, 0.05) !important;
  animation: borderGlow 3s infinite alternate;
}
@keyframes borderGlow {
  0% {
    border-color: rgba(63, 81, 181, 0.15);
  }
  100% {
    border-color: rgba(63, 81, 181, 0.35);
  }
}

.premium-hover-card {
  background: rgba(18, 18, 18, 0.92) !important;
  backdrop-filter: blur(25px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.7) !important;
}
.premium-avatar-glow {
  border: 2px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.05);
}

.shift-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.shift-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
}
.shift-stat-item.morning {
  border-bottom: 3px solid #00e676;
}
.shift-stat-item.intermediate {
  border-bottom: 3px solid #448aff;
}
.shift-stat-item.late {
  border-bottom: 3px solid #ff9100;
}
.stat-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.stat-details {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stat-label {
  font-size: 0.55rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.1;
}
.stat-value {
  font-size: 0.95rem;
  color: white;
  font-weight: 700;
  line-height: 1.1;
}
.assigned-days-list .text-overline {
  font-size: 0.6rem !important;
  letter-spacing: 0.5px !important;
  color: rgba(255, 255, 255, 0.4) !important;
  margin-bottom: 4px;
}

/* --- VISUAL POLISH STYLING --- */

/* Custom styles for Spezialtag button */
.special-day-btn {
  background: rgba(0, 230, 118, 0.03) !important;
  border: 1px solid rgba(0, 230, 118, 0.15) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  opacity: 0.45;
}
.weekly-day-row:hover .special-day-btn {
  opacity: 1;
  background: rgba(0, 230, 118, 0.08) !important;
  border-color: rgba(0, 230, 118, 0.4) !important;
  box-shadow: 0 0 10px rgba(0, 230, 118, 0.15) !important;
}

/* Pulsing Status Dots in Header */
.pulsing-dot-green {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #00e676;
  box-shadow: 0 0 6px #00e676;
  animation: pulseGreen 2s infinite;
}
@keyframes pulseGreen {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}

.pulsing-dot-indigo {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #2979ff;
  box-shadow: 0 0 6px #2979ff;
  animation: pulseIndigo 2s infinite;
}
@keyframes pulseIndigo {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}

.drawer-header-pill {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 8px !important;
  font-size: 0.7rem !important;
  letter-spacing: 0.1px;
}

/* Team-Radar: produktiver Leerzustand des Assistenten */
.team-radar-drawer-header {
  min-height: 132px;
  padding: 22px 24px !important;
}
.team-radar-status-badge {
  padding: 4px 10px !important;
  font-size: 0.76rem !important;
  line-height: 1.25;
}
.team-radar-status-badge .pulsing-dot-green {
  width: 8px;
  height: 8px;
}
.team-radar-title {
  margin-top: 10px !important;
  font-size: 1.5rem !important;
  line-height: 1.2;
}
.team-radar-subtitle {
  margin-top: 6px !important;
  font-size: 0.8rem !important;
  line-height: 1.35;
}
.team-radar-drawer-body {
  padding: 22px !important;
}
.team-radar {
  min-height: 0;
  gap: 16px;
}
.team-radar-callout {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 16px;
  border: 1px solid rgba(92, 112, 255, 0.2);
  border-radius: 15px;
  background: linear-gradient(135deg, rgba(92, 112, 255, 0.09), rgba(56, 189, 248, 0.035));
}
.team-radar-callout-icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #91a0ff;
  background: rgba(92, 112, 255, 0.14);
}
.team-radar-callout-icon .v-icon {
  color: currentColor !important;
  font-size: 22px !important;
}
.team-radar-callout > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.team-radar-callout strong {
  color: #f5f7fa;
  font-size: 0.94rem;
  line-height: 1.25;
}
.team-radar-callout span:not(.team-radar-callout-icon) {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.77rem;
  line-height: 1.35;
}
.team-radar-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}
.team-radar-summary-card {
  display: grid;
  min-width: 0;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 1px 9px;
  padding: 13px 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
}
.team-radar-summary-card .v-icon {
  grid-row: 1 / span 2;
  color: #8c9eff;
  font-size: 21px !important;
}
.team-radar-summary-card.is-hours .v-icon {
  color: #ffb74d;
}
.team-radar-summary-card.is-open-shifts .v-icon {
  color: #ef9a9a;
}
.team-radar-summary-card.is-open-shifts.is-complete .v-icon {
  color: #81c784;
}
.team-radar-summary-card strong {
  overflow: hidden;
  color: #fff;
  font-size: 1.08rem;
  line-height: 1.1;
  text-overflow: ellipsis;
}
.team-radar-summary-card span {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.68rem;
  line-height: 1.2;
  white-space: normal;
}
.team-radar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.team-radar-filter {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 11px !important;
  background: rgba(255, 255, 255, 0.02) !important;
}
.team-radar-filter .v-btn {
  min-width: 0 !important;
  height: 34px !important;
  padding: 0 13px !important;
  color: rgba(255, 255, 255, 0.48) !important;
  background: transparent !important;
  font-size: 0.73rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}
.team-radar-filter .v-btn.v-btn--active {
  color: #a5d6a7 !important;
  background: rgba(76, 175, 80, 0.12) !important;
}
.team-radar-sort {
  width: 34px !important;
  height: 34px !important;
  color: rgba(255, 255, 255, 0.5) !important;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(255, 255, 255, 0.025) !important;
}
.team-radar-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
}
.team-radar-person {
  position: relative;
  overflow: hidden;
  padding: 14px 15px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-left: 4px solid #ffb74d;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.015));
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.team-radar-person:hover {
  z-index: 1;
  transform: translateY(-2px);
  border-color: rgba(140, 158, 255, 0.32);
  background: linear-gradient(135deg, rgba(140, 158, 255, 0.08), rgba(255, 255, 255, 0.025));
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.28);
}
.team-radar-person.is-balanced {
  border-left-color: #4caf50;
}
.team-radar-person.is-over {
  border-left-color: #ef5350;
}
.team-radar-person.is-neutral {
  border-left-color: #78909c;
}
.team-radar-person-head {
  display: flex;
  align-items: center;
  gap: 11px;
}
.team-radar-avatar {
  flex: 0 0 auto;
  border: 2px solid rgba(255, 255, 255, 0.14);
  background: #263238;
  box-shadow: 0 5px 14px rgba(0, 0, 0, 0.24);
}
.team-radar-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.team-radar-person-name {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}
.team-radar-person-name strong {
  overflow: hidden;
  color: #f5f7fa;
  font-size: 0.9rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-radar-person-name span {
  overflow: hidden;
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.38);
  font-size: 0.69rem;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-radar-balance {
  flex: 0 0 auto;
  max-width: 132px;
  overflow: hidden;
  padding: 5px 9px;
  border: 1px solid rgba(255, 183, 77, 0.22);
  border-radius: 999px;
  color: #ffcc80;
  background: rgba(255, 183, 77, 0.08);
  font-size: 0.68rem;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-radar-balance.is-balanced {
  border-color: rgba(76, 175, 80, 0.25);
  color: #81c784;
  background: rgba(76, 175, 80, 0.09);
}
.team-radar-balance.is-over {
  border-color: rgba(239, 83, 80, 0.25);
  color: #ef9a9a;
  background: rgba(239, 83, 80, 0.09);
}
.team-radar-balance.is-neutral {
  border-color: rgba(120, 144, 156, 0.25);
  color: #b0bec5;
  background: rgba(120, 144, 156, 0.09);
}
.team-radar-hours-summary {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
}
.team-radar-hours-summary > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.team-radar-hours-summary span {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.63rem;
  font-weight: 750;
  letter-spacing: 0.06em;
  line-height: 1.2;
  text-transform: uppercase;
}
.team-radar-hours-summary strong {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  line-height: 1.2;
}
.team-radar-hours-summary strong small {
  color: rgba(255, 255, 255, 0.43);
  font-size: 0.7rem;
  font-weight: 650;
}
.team-radar-hours-summary > b {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.86rem;
  line-height: 1.2;
}
.team-radar-progress {
  height: 7px;
  overflow: hidden;
  margin-top: 8px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.07);
}
.team-radar-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #fb8c00, #ffca28);
}
.team-radar-progress span.is-balanced {
  background: linear-gradient(90deg, #2e7d32, #66bb6a);
}
.team-radar-progress span.is-over {
  background: linear-gradient(90deg, #d32f2f, #ef5350);
}
.team-radar-progress span.is-neutral {
  background: #78909c;
}
.team-radar-kpis {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 11px;
}
.team-radar-kpis span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  padding: 5px 8px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 9px;
  color: rgba(255, 255, 255, 0.58);
  background: rgba(255, 255, 255, 0.025);
  font-size: 0.69rem;
  font-weight: 700;
  line-height: 1.2;
}
.team-radar-kpis .v-icon {
  color: currentColor !important;
}
.team-radar-kpis .is-match {
  border-color: rgba(92, 112, 255, 0.14);
  color: #aeb8ff;
  background: rgba(92, 112, 255, 0.065);
}
.team-radar-kpis .is-complete {
  border-color: rgba(76, 175, 80, 0.16);
  color: #81c784;
  background: rgba(76, 175, 80, 0.065);
}
.team-radar-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 10px;
  padding: 8px 9px;
  border: 1px solid rgba(255, 193, 7, 0.14);
  border-radius: 9px;
  color: #ffca28;
  background: rgba(255, 193, 7, 0.045);
  font-size: 0.68rem;
}
.team-radar-warning .v-icon {
  color: currentColor !important;
}
.team-radar-warning span {
  overflow: hidden;
  flex: 1 1 auto;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-radar-warning small {
  flex: 0 0 auto;
  font-weight: 800;
}
.team-radar-empty {
  display: flex;
  min-height: 150px;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  color: rgba(255, 255, 255, 0.38);
  font-size: 0.8rem;
  text-align: center;
}

/* Glassmorphic Empty State Card */
.empty-state-glass-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4), inset 0 0 16px rgba(255, 255, 255, 0.02) !important;
  position: relative;
  overflow: hidden;
}
.empty-state-glass-card::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(140, 158, 255, 0.06) 0%, transparent 60%);
  pointer-events: none;
}

/* Glowing Icon Circle */
.glowing-icon-wrapper {
  width: 50px;
  height: 50px;
  margin: 0 auto;
  border-radius: 50%;
  background: rgba(140, 158, 255, 0.08);
  border: 1px solid rgba(140, 158, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 20px rgba(140, 158, 255, 0.05);
}

/* Steps Guideline Cards */
.guide-step-card {
  background: rgba(255, 255, 255, 0.01) !important;
  border: 1px solid rgba(255, 255, 255, 0.04) !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.guide-step-card:hover {
  background: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  transform: translateX(4px);
}

::v-deep.menuable__content--active {
  z-index: 2000 !important;
}

.step-num-badge {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: rgba(140, 158, 255, 0.15);
  border: 1px solid rgba(140, 158, 255, 0.3);
  color: #8c9eff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: bold;
}

/* LIGHT MODE STYLES OVERRIDES */
.assistant-root.light-mode {
  background: #f5f5f7;
}
.assistant-root.light-mode .assistant-bar {
  background: rgba(255, 255, 255, 0.8) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.assistant-root.light-mode .assistant-title {
  color: #1d1d1f !important;
}
.assistant-root.light-mode .assistant-subtitle {
  color: rgba(0, 0, 0, 0.45) !important;
}
.assistant-root.light-mode .assistant-back-btn,
.assistant-root.light-mode .nav-btn {
  background: rgba(0, 0, 0, 0.04) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  color: #1d1d1f !important;
}
.assistant-root.light-mode .assistant-back-btn:hover,
.assistant-root.light-mode .nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 0, 0, 0.15) !important;
}
.assistant-root.light-mode .week-pill {
  background: rgba(76, 175, 80, 0.08) !important;
  border: 1px solid rgba(76, 175, 80, 0.2) !important;
  color: #2e7d32 !important;
}
.assistant-root.light-mode .weekly-day-row {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}
.assistant-root.light-mode .weekly-day-row:hover {
  background: #fafafa !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
.assistant-root.light-mode .today-row {
  border: 1px solid rgba(76, 175, 80, 0.35) !important;
  background: rgba(76, 175, 80, 0.03) !important;
}
.assistant-root.light-mode .weekend-row {
  background: rgba(255, 152, 0, 0.02) !important;
  border-color: rgba(255, 152, 0, 0.08) !important;
}
.assistant-root.light-mode .weekend-row:hover {
  background: rgba(255, 152, 0, 0.04) !important;
}
.assistant-root.light-mode .shift-card {
  background: #fafafa !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.assistant-root.light-mode .shift-unassigned {
  border-color: rgba(255, 152, 0, 0.2) !important;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.04), rgba(255, 152, 0, 0.01)) !important;
}
.assistant-root.light-mode .shift-unassigned:hover {
  border-color: rgba(255, 152, 0, 0.5) !important;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.08), rgba(255, 152, 0, 0.03)) !important;
}
.assistant-root.light-mode .shift-assigned {
  border-color: rgba(0, 0, 0, 0.06) !important;
  background: #ffffff !important;
}
.assistant-root.light-mode .shift-assigned:hover {
  border-color: rgba(0, 0, 0, 0.15) !important;
  background: #fafafa !important;
}
.assistant-root.light-mode .shift-selected {
  border-color: #1976d2 !important;
  background: rgba(25, 118, 210, 0.06) !important;
  box-shadow: 0 0 12px rgba(25, 118, 210, 0.1) !important;
}
.assistant-root.light-mode .assistant-drawer {
  background: #ffffff !important;
  border-left: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.05) !important;
}
.assistant-root.light-mode .drawer-header {
  background: #ffffff !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.assistant-root.light-mode .empty-state-glass-card {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04) !important;
}
.assistant-root.light-mode .reco-card {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03) !important;
}
.assistant-root.light-mode .roody-insight-box {
  background: rgba(76, 175, 80, 0.08) !important;
  border-color: rgba(76, 175, 80, 0.25) !important;
}
.assistant-root.light-mode .guide-step-card {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02) !important;
}
.assistant-root.light-mode .guide-step-card:hover {
  background: #fafafa !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}
.assistant-root.light-mode .team-radar-callout {
  border-color: rgba(63, 81, 181, 0.16);
  background: linear-gradient(135deg, rgba(63, 81, 181, 0.07), rgba(3, 169, 244, 0.025));
}
.assistant-root.light-mode .team-radar-callout strong,
.assistant-root.light-mode .team-radar-summary-card strong,
.assistant-root.light-mode .team-radar-person-name strong {
  color: #1f2937;
}
.assistant-root.light-mode .team-radar-callout span:not(.team-radar-callout-icon),
.assistant-root.light-mode .team-radar-summary-card span,
.assistant-root.light-mode .team-radar-person-name span,
.assistant-root.light-mode .team-radar-hours-summary span,
.assistant-root.light-mode .team-radar-hours-summary strong small,
.assistant-root.light-mode .team-radar-kpis span {
  color: rgba(31, 41, 55, 0.52);
}
.assistant-root.light-mode .team-radar-summary-card,
.assistant-root.light-mode .team-radar-person {
  border-color: rgba(31, 41, 55, 0.08);
  background: #fff;
  box-shadow: 0 3px 12px rgba(31, 41, 55, 0.035);
}
.assistant-root.light-mode .team-radar-person:hover {
  border-color: rgba(63, 81, 181, 0.24);
  background: #fafbff;
  box-shadow: 0 8px 22px rgba(31, 41, 55, 0.09);
}
.assistant-root.light-mode .team-radar-person.is-under {
  border-left-color: #fb8c00;
}
.assistant-root.light-mode .team-radar-person.is-balanced {
  border-left-color: #43a047;
}
.assistant-root.light-mode .team-radar-person.is-over {
  border-left-color: #e53935;
}
.assistant-root.light-mode .team-radar-person.is-neutral {
  border-left-color: #78909c;
}
.assistant-root.light-mode .team-radar-filter {
  border-color: rgba(31, 41, 55, 0.09);
  background: #f5f7fa !important;
}
.assistant-root.light-mode .team-radar-filter .v-btn {
  color: rgba(31, 41, 55, 0.55) !important;
}
.assistant-root.light-mode .team-radar-sort {
  color: rgba(31, 41, 55, 0.55) !important;
  border-color: rgba(31, 41, 55, 0.09);
  background: #fff !important;
}
.assistant-root.light-mode .team-radar-hours-summary strong {
  color: rgba(31, 41, 55, 0.82);
}
.assistant-root.light-mode .team-radar-hours-summary > b {
  color: rgba(31, 41, 55, 0.62);
}
.assistant-root.light-mode .team-radar-progress {
  background: rgba(31, 41, 55, 0.08);
}
.assistant-root.light-mode .team-radar-kpis span {
  border-color: rgba(31, 41, 55, 0.08);
  background: #f7f8fa;
}
.assistant-root.light-mode .team-radar-kpis .is-match {
  border-color: rgba(63, 81, 181, 0.14);
  color: #4f5fa7;
  background: rgba(63, 81, 181, 0.055);
}
.assistant-root.light-mode .team-radar-kpis .is-complete {
  border-color: rgba(46, 125, 50, 0.15);
  color: #2e7d32;
  background: rgba(46, 125, 50, 0.055);
}
.assistant-root.light-mode .team-radar-warning {
  border-color: rgba(245, 124, 0, 0.16);
  color: #b45309;
  background: rgba(245, 124, 0, 0.055);
}
.assistant-root.light-mode .team-radar-empty {
  color: rgba(31, 41, 55, 0.46);
}
.assistant-root.light-mode .section-title {
  color: rgba(0, 0, 0, 0.45) !important;
}
.assistant-root.light-mode .hover-row {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.assistant-root.light-mode .hover-row:hover {
  background: #fafafa !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}
.assistant-root.light-mode .white--text {
  color: #1d1d1f !important;
}
.assistant-root.light-mode .grey--text {
  color: rgba(0, 0, 0, 0.5) !important;
}
.assistant-root.light-mode .stat-value {
  color: #1d1d1f !important;
}
.assistant-root.light-mode .border-right {
  border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.assistant-root.light-mode .week-fraction-badge {
  background: rgba(25, 118, 210, 0.08) !important;
  border: 1px solid rgba(25, 118, 210, 0.2) !important;
  color: #1976d2 !important;
}
.assistant-root.light-mode .grey.darken-4 {
  background-color: #f5f5f7 !important;
}
.assistant-root.light-mode .grey.darken-3 {
  background-color: #ffffff !important;
}
.assistant-root.light-mode .grey.darken-2 {
  background-color: #e5e5ea !important;
}

/* Responsive adjustments for smaller screens (like 14-inch displays) */
@media (max-width: 1440px) {
  .assistant-drawer {
    width: 480px !important;
    flex-basis: 480px;
  }
  .drawer-body {
    padding: 16px !important;
  }
  .team-radar-drawer-body {
    padding: 20px !important;
  }
  .reco-card {
    padding: 12px !important;
  }
  .section-title {
    font-size: 0.68rem !important;
    margin-bottom: 6px !important;
  }
  .roody-insight-box {
    margin-top: 8px !important;
    padding: 8px !important;
  }
  .reco-card .v-btn {
    margin-top: 8px !important;
  }
}

@media (max-width: 1100px) {
  .assistant-drawer {
    width: min(480px, 48vw) !important;
    flex-basis: min(480px, 48vw);
  }
  .team-radar-drawer-header {
    padding: 20px !important;
  }
  .team-radar-drawer-body {
    padding: 18px !important;
  }
}
</style>
